import React, { useEffect, useRef, useState } from "react";
import Layout from "../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { MASTER_API, PURCHASE_API } from "../apiConfig";
import { authHeaders } from "../utils/authHeaders";
import { useFieldPermissions } from "../utils/useFieldPermissions";
import MaskedValue from "../Component/MaskedValue";
import html2pdf from "html2pdf.js";
import InspectionReportView from "../ProcurementManagement/ProductManagement/InspectionReportView";
import RawMaterialInspectionReportView from "../ProcurementManagement/ProductManagement/RawMaterialInspectionReportView";
import { FileText, Copy, Check } from 'lucide-react';

const INSPECTION_API = `${MASTER_API}/inspection`;
const QUALITY_INSP_API = `${PURCHASE_API}/quality-inspection`;

// The inspection detail/report routes use __ as a safe separator for slashes in
// inspection numbers (e.g. MIM/QA/01 → MIM__QA__01), because Express splits
// URL-encoded %2F into real path segments and causes a 500.
const safeInspNo = (no) => String(no || "").replace(/\//g, "__");

/* ── FORMULA AUTO-CALC (Quality Inspection) ────────────────────────────
   A row whose template Formula = Yes is calculated from the ACTUAL values
   typed on the variable rows, separately for every sample column
   (Sample 1 of the formula uses Sample 1 of the variables, and so on).
   No eval(): a tiny parser for numbers, variables, + - * / and brackets.
   Blank while any needed variable is empty / not a plain number, or on
   divide-by-zero. A variable row locked to Sample 1 (Sample Requirement =
   No) supplies its single value to every sample column. */
// FORMULA_CALC_START
const toNumF = (s) => {
  const t = String(s ?? "").trim();
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : null;
};

const evalFormulaExpr = (formula, values) => {
  const src = String(formula || "");
  const tokens = src.match(/\d+\.?\d*|\.\d+|[A-Za-z_][A-Za-z0-9_]*|[-+*/()]/g);
  if (!tokens || tokens.join("").length !== src.replace(/\s+/g, "").length) return null;
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expr = () => {
    let v = term();
    while (peek() === "+" || peek() === "-") { const op = next(); const r = term(); v = op === "+" ? v + r : v - r; }
    return v;
  };
  const term = () => {
    let v = factor();
    while (peek() === "*" || peek() === "/") { const op = next(); const r = factor(); v = op === "*" ? v * r : v / r; }
    return v;
  };
  const factor = () => {
    const t = next();
    if (t === undefined) throw new Error("incomplete");
    if (t === "-") return -factor();
    if (t === "+") return factor();
    if (t === "(") { const v = expr(); if (next() !== ")") throw new Error("bracket"); return v; }
    if (/^[A-Za-z_]/.test(t)) {
      const v = values[t.toLowerCase()];
      if (v === undefined) throw new Error("missing variable");
      return v;
    }
    return parseFloat(t);
  };
  try {
    const v = expr();
    return pos === tokens.length && Number.isFinite(v) ? v : null;
  } catch (e) { return null; }
};

// Returns the rows array with every formula row's samples (and its
// Pass/Fail) recalculated. Returns the SAME array/rows when nothing changed.
function applyFormulaRows(rows) {
  if (!rows.some((r) => r.isFormula)) return rows;
  const sampleCount = Math.max(0, ...rows.map((r) => (r.samples || []).length));
  return rows.map((row) => {
    if (!row.isFormula) return row;
    const samples = Array.from({ length: sampleCount }, (_, s) => {
      if (row.sampleRequirement === "No" && s > 0) return "";
      const vars = {};
      rows.forEach((v) => {
        if (v.isFormula || !v.variable) return;
        const idx = v.sampleRequirement === "No" ? 0 : s;
        const n = toNumF((v.samples || [])[idx]);
        if (n !== null) vars[String(v.variable).trim().toLowerCase()] = n;
      });
      const res = evalFormulaExpr(row.formula, vars);
      return res === null ? "" : String(parseFloat(res.toFixed(6)));
    });
    const old = row.samples || [];
    if (samples.length === old.length && samples.every((x, i) => x === (old[i] || ""))) return row;
    const updated = { ...row, samples };
    updated.manualResult = computeAutoResult(updated);
    return updated;
  });
}
// FORMULA_CALC_END

// Local yyyy-mm-dd for today, used to default date pickers (e.g. Assign
// Drum's Date field) instead of leaving them blank. Built from local
// getFullYear/Month/Date rather than toISOString() so it reflects the
// browser's local date, not UTC (which can be a day off near midnight).
const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ─── dd-mm-yyyy DATE INPUT ───────────────────────────────────────────────────
// A native <input type="date"> always renders in the browser/OS locale format
// (e.g. yyyy-mm-dd or mm/dd/yyyy) and that can't be changed from code. So this
// shows a text field in dd-mm-yyyy instead, with a calendar button that opens
// the native picker. The value going in/out is still yyyy-mm-dd, so state and
// backend payloads stay exactly as they were.
const isoToDMY = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

const dmyToISO = (dmy) => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dmy || "");
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  // reject things like 31-02-2026 that JS would silently roll over
  if (d.getFullYear() !== Number(yyyy) || d.getMonth() !== Number(mm) - 1 || d.getDate() !== Number(dd)) return "";
  return `${yyyy}-${mm}-${dd}`;
};

function DateInputDMY({ value, onChange, style }) {
  const [text, setText] = useState(isoToDMY(value));
  const pickerRef = useRef(null);

  // Sync when the parent value changes from outside (API load, picker),
  // but don't wipe a half-typed date (which maps to "" in the parent).
  useEffect(() => {
    if (dmyToISO(text) !== (value || "")) setText(isoToDMY(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleType = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let out = digits.slice(0, 2);
    if (digits.length > 2) out += "-" + digits.slice(2, 4);
    if (digits.length > 4) out += "-" + digits.slice(4);
    setText(out);
    onChange(dmyToISO(out));
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (e) { /* fall through */ }
    }
    el.focus();
    el.click();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder="dd-mm-yyyy"
        maxLength={10}
        style={{ ...style, paddingRight: 34 }}
        value={text}
        onChange={handleType}
      />
      <button
        type="button"
        onClick={openPicker}
        title="Pick a date"
        style={{
          position: "absolute", right: 1, top: 1, bottom: 1, width: 32,
          border: "none", background: "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", color: "#475569",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {/* hidden native picker, only used for the calendar popup */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, pointerEvents: "none" }}
      />
    </div>
  );
}

// Reads the QUALITY_INSPECTION permissions the login response stored, e.g.
// [{ module_id: "QUALITY_INSPECTION", can_view: 1, can_edit: 1 }, ...]
// Same shape/localStorage keys as Goodsreceipt.js's getGRNPermission and
// GatePass.js's getGatePassPermission — admins always pass. This module has
// no manual "create" flow, so only can_view / can_edit apply.
function getQualityInspectionPermission(moduleId) {
  const none = { can_view: false, can_edit: false };
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user) return none;
    if (String(user.role || "").toLowerCase() === "admin") {
      return { can_view: true, can_edit: true };
    }
    // Same source as Purchase Request's getModulePermissions: the
    // permissions array stored on the "user" object at login.
    const perms = user.permissions || JSON.parse(localStorage.getItem("permissions") || "[]");
    const qi = perms.find((p) => p.module_id === moduleId || p.module_id === "QUALITY_INSPECTION" || p.module_id === "QM_INSPECTION");
    return { can_view: qi?.can_view === 1 || qi?.can_view === true, can_edit: qi?.can_edit === 1 || qi?.can_edit === true };
  } catch (e) {
    console.error("Error reading Quality Inspection permissions:", e);
    return none;
  }
}

// View/Edit modal label -> FIELD_REGISTRY.QUALITY_INSPECTION field_id.
// Labels not listed here are never masked.
const QI_LABEL_TO_FIELD = {
  "Inspection Number": "format_number",
  "Test Type": "test_type",
  "Product Code": "product_code",
  "Product Description": "product_description",
  "Batch No": "batch_number",
  "Quantity to Inspect": "inspect_qty",
};
const QI_MASK = "••••••";

// Field-visibility (Display role) is looked up per module_id. The Store Check
// screen (scope="STORE") is configured in Create Role under its OWN module, so
// it must not be looked up under QUALITY_INSPECTION - otherwise the role has
// no matching entry and every field comes back masked.
// Module ids = the tile ids in TILE_REGISTRY, one per screen.
const QI_MODULE_IDS = {
  FG: "QM_INSPECTION_FG",
  RM: "QM_INSPECTION",
  STORE: "QM_STORE_CHECK",
};
const qiModuleId = (scope) => QI_MODULE_IDS[scope] || "QM_INSPECTION";

// po_header.customer_name is often stored as "CODE - Customer Name" (e.g.
// "C5903662 - ABB INDIA LIMITED"). The UI should only show the name part.
const displayCustomerName = (name) => {
  if (!name) return name;
  const idx = String(name).indexOf(" - ");
  return idx !== -1 ? String(name).slice(idx + 3).trim() : name;
};

const TYPE_OPTIONS = ["Min", "Max", "Range", "Integer", "Decimal", "Text"];
// Fallback when a batch has no custom Sample No / labels configured yet —
// keeps every existing report looking exactly as it did before.
// Starting point is just 1 sample column — widening this (via the "Sample
// No" popup) only actually takes effect per-row for characteristics whose
// process has Sample Requirement = "Yes" (see sampleRequirement handling
// below); everything else stays locked to Sample 1.
const DEFAULT_SAMPLE_LABELS = ["Sample. 1"];

// Quantities come back from the DB as fixed 3-decimal strings (e.g.
// "125.900", "6.370"). For display, cap at 2 decimal places and drop a
// trailing ".00"/".0" entirely (so "125.900" -> "125.9", "50.000" -> "50").
// Non-numeric values (blank, text) are returned unchanged.
const formatQty = (val) => {
  if (val === null || val === undefined || val === "") return val;
  const num = Number(val);
  if (Number.isNaN(num)) return val;
  return num.toFixed(2).replace(/\.?0+$/, "");
};

// Always renders dd/mm/yyyy, regardless of the viewer's OS/browser locale.
// toLocaleDateString() with no locale argument follows the VIEWER's system
// locale — en-IN/en-GB machines happen to show d/m/yyyy, but en-US machines
// show m/d/yyyy, so the same date looked different for different people.
// This builds the string manually so it's the same for everyone everywhere.
const formatDateDMY = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toOptions = (list = []) =>
  list.map((item) => {
    const v = item.productCode || item.product_code || item.name || item;
    return { value: v, label: v };
  });

// Converts configuration-master API responses (construction/code/cable-type/
// category/core/colour/cross-section/reference-of-standard/voltage) into
// {value,label} options — same shape the config endpoints return across
// the app (sometimes plain strings, sometimes {name|code|...} objects).
const toFilterOptions = (list) => {
  if (!list || !Array.isArray(list)) return [];
  // De-duplicate (case-insensitive) so repeated master-data entries such as
  // "BEIGE" / "BEIGE" only show up once in the dropdown.
  const seen = new Set();
  return list
    .map((item) => {
      let val;
      if (item === null || item === undefined) {
        val = "";
      } else if (typeof item === "string" || typeof item === "number") {
        // Numbers matter for Cores (1, 2, 3, 4 ...) — previously dropped.
        val = item;
      } else {
        val =
          item.name ?? item.code ?? item.cable_type ?? item.category ??
          item.core ?? item.cores ?? item.noOfCores ?? item.no_of_cores ??
          item.colour ?? item.color ?? item.value ?? "";
      }
      const s = String(val).trim();
      return { value: s, label: s };
    })
    .filter((opt) => {
      if (opt.value === "") return false;
      const key = opt.value.toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// A stock/batch row carries a product_code; the row itself may or may not
// already have the product's attribute columns joined in. This resolves an
// attribute for a row by checking the row first, then falling back to the
// productdetails lookup (built from the product master list), trying a few
// possible column-name variants for each attribute.
const ATTR_FIELD_CANDIDATES = {
  construction:         ["construction"],
  code:                 ["code"],
  cableType:            ["cableType", "cable_type"],
  category:             ["productCategory", "category"],
  noOfCores:            ["noOfCores", "no_of_cores", "cores", "core"],
  color:                ["color", "colour"],
  nominalCrossSection:  ["nominalCrossSection", "nominal_cross_section"],
  referenceOfStandard:  ["referenceOfStandard", "reference_of_standard"],
  voltage:              ["voltage"],
};

const getProductAttr = (row, productAttrByCode, attrKey) => {
  const candidates = ATTR_FIELD_CANDIDATES[attrKey] || [attrKey];
  for (const field of candidates) {
    if (row && row[field] !== undefined && row[field] !== null && row[field] !== "") {
      return String(row[field]);
    }
  }
  const prod = productAttrByCode[(row?.product_code || "").toLowerCase()];
  if (prod) {
    for (const field of candidates) {
      if (prod[field] !== undefined && prod[field] !== null && prod[field] !== "") {
        return String(prod[field]);
      }
    }
  }
  return "";
};

const customSelectStyles = {
  control: (b) => ({ ...b, minHeight: 32, height: 32, fontSize: "13px" }),
  valueContainer: (b) => ({ ...b, height: "32px", padding: "0 8px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0 }),
  dropdownIndicator: (b) => ({ ...b, padding: 4 }),
  menu: (b) => ({ ...b, fontSize: "13px", zIndex: 9999 }),
};

// ─── TYPE-AWARE VALIDATION ────────────────────────────────────────────────────
function samplePassesForType(valueType, standard, actual) {
  if (!standard || String(standard).trim() === "") return true;
  if (!actual   || String(actual).trim()   === "") return false;

  const std  = String(standard).trim();
  const act  = String(actual).trim();
  const type = (valueType || "Text").trim();

  // Standard values are sometimes stored with a text prefix baked in
  // (e.g. "Min. 32", "Max. 0.017241") instead of a bare number. Strip any
  // leading non-numeric characters so parseFloat can find the real value.
  const extractNumber = (s) => {
    const match = String(s).match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : NaN;
  };

  switch (type) {
    case "Min": {
      const stdNum = extractNumber(std);
      const actNum = parseFloat(act);
      if (isNaN(stdNum) || isNaN(actNum)) return false;
      return actNum >= stdNum;
    }
    case "Max": {
      const stdNum = extractNumber(std);
      const actNum = parseFloat(act);
      if (isNaN(stdNum) || isNaN(actNum)) return false;
      return actNum <= stdNum;
    }
    case "Range": {
      const match = std.match(/^([+-]?[\d.]+)\s*(?:-|to)\s*([+-]?[\d.]+)$/i);
      if (!match) return false;
      const lo     = parseFloat(match[1]);
      const hi     = parseFloat(match[2]);
      const actNum = parseFloat(act);
      if (isNaN(lo) || isNaN(hi) || isNaN(actNum)) return false;
      return actNum >= Math.min(lo, hi) && actNum <= Math.max(lo, hi);
    }
    case "Integer": {
      const stdInt = parseInt(std, 10);
      const actInt = parseInt(act, 10);
      if (isNaN(stdInt) || isNaN(actInt)) return false;
      return actInt === stdInt;
    }
    case "Decimal":
    case "Expo": {
      const stdNum = parseFloat(std);
      const actNum = parseFloat(act);
      if (isNaN(stdNum) || isNaN(actNum)) return false;
      return actNum === stdNum;
    }
    case "Text":
    default:
      return act.trim().toLowerCase() === std.trim().toLowerCase();
  }
}

// samples: array of any length (was fixed to s1,s2,s3 — now dynamic per batch)
function anySamplePasses(valueType, standard, samples) {
  if (!standard || String(standard).trim() === "") return true;
  return (samples || []).some((s) => samplePassesForType(valueType, standard, s));
}

// Back-compat shim: a row that still only has sample1/2/3 (no .samples array)
// falls back to those three values, so nothing else has to change.
function rowSamples(row) {
  if (Array.isArray(row.samples)) return row.samples;
  return [row.sample1, row.sample2, row.sample3];
}

function computeAutoResult(row) {
  let samples = rowSamples(row);
  // Sample Requirement = "No" (anything other than an explicit "Yes") → only
  // Sample 1 counts toward Pass/Fail, even if stale values exist in later
  // sample columns from an earlier save.
  if (row.sampleRequirement === "No") samples = samples.slice(0, 1);
  const has = samples.some((s) => s);
  if (!has) return "";  // No samples entered → no result yet
  if (!row.standardValue || String(row.standardValue).trim() === "") return "pass"; // No standard = always pass
  return anySamplePasses(row.type, row.standardValue, samples)
    ? "pass"
    : "fail";
}

function sampleMatchColor(valueType, standard, actual) {
  if (!actual || String(actual).trim() === "") return null;
  return samplePassesForType(valueType, standard, actual) ? "pass" : "fail";
}

// ─── ACCEPT / REJECT DIALOG ───────────────────────────────────────────────────
// Shown right after the actual values are saved, BEFORE the certificate step.
// Accept  → continues into the existing match/mismatch → certificate flow.
// Reject  → stock row is marked fully Rejected (no certificate at all), and
//           the test type being inspected is stored against the stock row.
//eslint-disable-next-line
function AcceptRejectDialog({ testType, onAccept, onReject, onCancel, saving }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 460, width: "90%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", margin: "0 auto 12px",
            background: "#334155",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontSize: 26, fontWeight: 700, lineHeight: 1 }}>?</span>
          </div>
          <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Accept or Reject this Batch?
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
            {testType
              ? <>Test type: <strong>{testType}</strong>. </>
              : null}
            Accept to continue and generate the test certificate, or Reject to mark this
            batch as fully <strong>Rejected</strong> — no certificate will be generated.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button
            onClick={onAccept}
            disabled={saving}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              background: "#16a34a", color: "#fff",
              fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
            }}>
            Accept
          </button>
          <button
            onClick={onReject}
            disabled={saving}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              background: "#dc2626", color: "#fff",
              fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
            }}>
            {saving ? "Rejecting…" : "Reject"}
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8,
              border: "1px solid #cbd5e1", background: "#f8fafc",
              color: "#334155", fontSize: 14, fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
            }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────
function ConfirmDialog({ allMatch, onGenerateCert, onSkip, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 460, width: "90%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", margin: "0 auto 12px",
            background: allMatch ? "#16a34a" : "#dc2626",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
              {allMatch ? "✓" : "✗"}
            </span>
          </div>
          <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            {allMatch ? "All Parameters Match!" : "Some Parameters Don't Match"}
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
            {allMatch
              ? "All characteristics match their standard values. Would you like to generate the test certificate?"
              : "Some characteristics do not match their standard values. Do you still want to generate the test certificate and mark as Inspected?"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button
            onClick={onGenerateCert}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "none", cursor: "pointer",
              background: allMatch ? "#16a34a" : "#dc2626", color: "#fff",
              fontSize: 14, fontWeight: 600,
            }}>
            Yes, Generate Certificate
          </button>
          <button
            onClick={onSkip}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8,
              border: "1px solid #cbd5e1", background: "#f8fafc",
              color: "#334155", fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}>
            {allMatch ? "Save Without Certificate" : "No, Mark as Pending"}
          </button>
        </div>
        <button
          onClick={onCancel}
          style={{
            display: "block", width: "100%", marginTop: 10, padding: "8px 0",
            background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer",
          }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── BULK INSPECT CONFIRM DIALOG ──────────────────────────────────────────────
function BulkInspectConfirmDialog({ count, onConfirm, onReject, onCancel, saving }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999,
    }}>
      <div style={{
        position: "relative",
        background: "#fff", borderRadius: 16, padding: "32px 36px", maxWidth: 440, width: "90%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <button
          onClick={onCancel}
          disabled={saving}
          title="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            border: "none", background: "transparent", fontSize: 20, color: "#94a3b8",
            cursor: saving ? "not-allowed" : "pointer", width: 30, height: 30, borderRadius: 6, lineHeight: 1,
          }}>
          ✕
        </button>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", margin: "0 auto 12px",
            background: "#d97706",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontSize: 26, fontWeight: 700, lineHeight: 1 }}>!</span>
          </div>
          <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Mark {count} item{count > 1 ? "s" : ""} as Inspected?
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
            This will mark the selected product{count > 1 ? "s" : ""} as <strong>Inspected</strong> without
            a test certificate or recorded sample values. Are you sure you want to continue?
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button
            onClick={onConfirm}
            disabled={saving}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              background: "#d97706", color: "#fff",
              fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
            }}>
            {saving ? "Updating…" : "Inspected"}
          </button>
          <button
            onClick={onReject}
            disabled={saving}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8,
              border: "1px solid #cbd5e1", background: "#f8fafc",
              color: "#334155", fontSize: 14, fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
            }}>
            {saving ? "Updating…" : "Rejected"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── STATUS TABS ──────────────────────────────────────────────────────────────
function StatusTabs({ value, onChange }) {
  const tabs = [
    { key: "active",    label: "Active" },
    { key: "inspected", label: "Inspected" },
  ];
  return (
    <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
      {tabs.map((t) => (
        <button key={t.key} type="button" onClick={() => onChange(t.key)}
          style={{
            padding: "6px 20px", fontSize: 13, fontWeight: 600, borderRadius: 6, border: "none",
            cursor: "pointer", transition: "all 0.15s ease",
            background: value === t.key ? "#fff" : "transparent",
            color:      value === t.key ? "#0f172a" : "#64748b",
            boxShadow:  value === t.key ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
          }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── VIEW MODAL ───────────────────────────────────────────────────────────────
function ViewModal({ item, onClose, moduleId = "QM_INSPECTION" }) {
  const { isFieldVisible } = useFieldPermissions(moduleId);
  const [detail, setDetail]                   = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [activeTab, setActiveTab]             = useState("particulars");
  const [viewDocFiles, setViewDocFiles]       = useState([]);
  const [viewingDoc, setViewingDoc]           = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [sampleValues, setSampleValues]       = useState({});
  const [sampleLabels, setSampleLabels]       = useState(DEFAULT_SAMPLE_LABELS);
  const [wiNo, setWiNo]                       = useState("");
  const [refNo, setRefNo]                     = useState("");
  const [testedBy, setTestedBy]               = useState("");
  const [witnessedBy, setWitnessedBy]         = useState("");
  const [witnessedDesignation, setWitnessedDesignation] = useState("");
  const [changeLogs, setChangeLogs]           = useState([]);
  const viewDocInputRef                       = React.useRef(null);

  // Change Log — same endpoint/shape EditModal's fetchLogs uses, so the
  // View modal shows the same history without needing Edit access.
  useEffect(() => {
    if (!item?.id) return;
    axios.get(`${QUALITY_INSP_API}/logs/${item.id}`, { headers: authHeaders() })
      .then((res) => { if (res.data.success) setChangeLogs(res.data.logs); })
      .catch((err) => console.error("Error fetching logs:", err));
  }, [item?.id]);

  useEffect(() => {
    const inspNo = item?.format_number;
    if (!inspNo) { setLoading(false); return; }
    Promise.all([
      // ✅ FIX 1: encode inspNo + pass productCode so we get only this product's characteristics
      axios.get(`${INSPECTION_API}/detail/${safeInspNo(inspNo)}`, { headers: authHeaders(), params: { productCode: item.product_code, testType: item.test_type },}),
      axios.get(`${QUALITY_INSP_API}/batch-actuals/${item.id}`, { headers: authHeaders(), params: { testType: item.test_type } }),
    ])
      .then(([detailRes, avRes]) => {
        const rows = detailRes.data || [];
        if (rows.length === 0) { setError(`No rows returned for: ${inspNo}`); return; }
        // Deduplicate by id — backend JOIN can return same row multiple times
        const seenIds = new Set();
        const uniqueRows = rows.filter(r => {
          const key = r.id ? String(r.id) : r.characteristic + '__' + r.test_type;
          if (seenIds.has(key)) return false;
          seenIds.add(key);
          return true;
        });
        setDetail({ header: uniqueRows[0], rows: uniqueRows });
        const avPayload = avRes.data || {};
        const avList    = Array.isArray(avPayload) ? avPayload : (avPayload.actuals || []);
        setWiNo(avPayload.wi_no   || "");
        setRefNo(avPayload.ref_no || "");
        setTestedBy(avPayload.tested_by || "");
        setWitnessedBy(avPayload.witnessed_by || "");
        setWitnessedDesignation(avPayload.witnessed_designation || "");

        setSampleLabels(
          Array.isArray(avPayload.sample_labels) && avPayload.sample_labels.length > 0
            ? avPayload.sample_labels
            : DEFAULT_SAMPLE_LABELS
        );
        const avMap     = {};
        avList.forEach((av) => {
          const values = Array.isArray(av.sample_values) && av.sample_values.length > 0
            ? av.sample_values
            : [av.sample1 || "", av.sample2 || "", av.sample3 || ""];
          avMap[av.characteristic_id] = {
            values,
            remarks: av.remarks || "",
          };
        });
        const initial = {};
        rows.forEach((r, i) => { initial[i] = avMap[r.id] ?? { values: [], remarks: "" }; });
        setSampleValues(initial);
      })
      .catch((e) => setError(e.response?.data?.message || `API error ${e.response?.status}`))
      .finally(() => setLoading(false));
  }, [item]);

  const isRawMaterial = (detail?.header?.product_type || "").toLowerCase() === "raw material";
  const viewBaseCols  = isRawMaterial
    ? ["Parameters", "Test Type", "UOM", "Type", "Standard Value"]
    : ["Process", "Sub Process", "Parameters", "Test Type", "UOM", "Type", "Standard Value"];

  const stockQtyText = (item?.stock_qty !== undefined && item?.stock_qty !== null && item?.stock_qty !== "")
    ? formatQty(item.stock_qty) : null;

  const mainFields = detail ? Object.entries({
    "Inspection Number":  detail.header.inspection_number,
    "Test Type":          item?.test_type || detail.header.inspection_type,
    "Product Code":       detail.header.product_code,
    "Product Description": item?.product_description || detail.header.product_description,
    "Doc No":             isRawMaterial ? item?.grn_number : item?.production_order_number,
    "Supplier":           isRawMaterial ? item?.supplier_name : displayCustomerName(item?.customer_name),
    "Batch No":           item?.batch_number,
    "Qty":                formatQty(item?.qty),
    "Quantity to Inspect": formatQty(item?.qty),
    "UOM":                item?.uom,
    ...(!isRawMaterial ? {
      "Sales Order No":  item?.sales_order,
      "Date of Testing": item?.date_of_testing ? formatDateDMY(item.date_of_testing) : "",
    } : {}),
  }).filter(([, v]) => v !== undefined && v !== null && v !== "") : [];

  // Cut Piece (read-only, view mode) — shown right after the main fields,
  // followed by a repeated UOM field (same value/style as the UOM row above).
  // Always shown (falls back to "Optional" like the edit-mode placeholder)
  // so the layout matches the edit modal even when no cut piece was entered.
  if (detail) {
    const cutPieceValue = item?.cut_piece_qty ?? detail?.header?.cut_piece_qty;
    mainFields.push([
      "Cut Piece",
      (cutPieceValue !== undefined && cutPieceValue !== null && cutPieceValue !== "")
        ? formatQty(cutPieceValue)
        : "Optional",
    ]);
    mainFields.push(["UOM ", item?.uom || ""]); // trailing space keeps this label distinct from "UOM" above
  }

  const headerFields = detail ? Object.entries({
    "Category":              detail.header.category,
    "Sub Category":          detail.header.sub_category,
    "QA Category":           detail.header.qa_category,
    "Cable Type":            detail.header.cable_type,
    "Construction":          detail.header.construction,
    "Code":                  detail.header.code,
    "No Of Cores":           detail.header.no_of_cores,
    "Nominal Cross Section": detail.header.nominal_cross_section,
    "Colour":                detail.header.colour,
    "Voltage":               detail.header.voltage,
  }).filter(([, v]) => v) : [];

  const filteredRows = detail ? detail.rows : [];
  //eslint-disable-next-line
  const allMatch =
    filteredRows.length > 0 &&
    filteredRows.every((r, idx) => {
      const sv = sampleValues[idx] || {};
      return anySamplePasses(r.value_type, r.standard_value, sv.values);
    });

  return (
    <div style={mStyles.backdrop}>
      <div style={mStyles.modal}>
        <div style={mStyles.modalHeader}>
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: "#0f172a" }}>Quality Inspection</h4>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={mStyles.modalBody}>
          {loading ? (
            <div style={mStyles.centreMsg}>Loading…</div>
          ) : error ? (
            <div style={{ ...mStyles.centreMsg, color: "#dc2626" }}>⚠️ {error}</div>
          ) : !detail ? (
            <div style={mStyles.centreMsg}>No data found for: <strong>{item?.format_number}</strong></div>
          ) : (
            <>
              <div className="row mb-3">
                <div className="col-md-3">
                  <label style={mStyles.fieldLabel}>Product Type</label>
                  <input type="text" className="form-control" value={detail.header.product_type || ""} readOnly
                    style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                </div>
                <div className="col-md-3">
                  <label style={mStyles.fieldLabel}>W.I. No</label>
                  <input type="text" className="form-control" value={!isFieldVisible("wi_no") ? QI_MASK : wiNo} readOnly
                    style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                </div>
                <div className="col-md-3">
                  <label style={mStyles.fieldLabel}>Ref</label>
                  <input type="text" className="form-control" value={!isFieldVisible("ref_no") ? QI_MASK : refNo} readOnly
                    style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                </div>
                <div className="col-md-3">
                  <label style={mStyles.fieldLabel}>Tested By</label>
                  <input type="text" className="form-control" value={testedBy} readOnly
                    style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                </div>
                {(item?.test_type || detail.header.test_type || "").toLowerCase() === "customer inspection" && (
                  <>
                    <div className="col-md-3">
                      <label style={mStyles.fieldLabel}>Witnessed By</label>
                      <input type="text" className="form-control" value={witnessedBy} readOnly
                        style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                    </div>
                    <div className="col-md-3">
                      <label style={mStyles.fieldLabel}>Designation</label>
                      <input type="text" className="form-control" value={witnessedDesignation} readOnly
                        style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                    </div>
                  </>
                )}
              </div>
              <div style={mStyles.sectionBox}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(140px, 1fr))", gap: 12 }}>
                  {[...mainFields, ...headerFields].map(([label, value], i) => (
                    <div key={i}>
                      <label style={mStyles.miniLabel}>{label.toUpperCase()}</label>
                      <input type="text" value={QI_LABEL_TO_FIELD[label] && !isFieldVisible(QI_LABEL_TO_FIELD[label]) ? QI_MASK : value} readOnly className="form-control form-control-sm"
                        style={{ height: 32, fontSize: 13, background: "#fff" }} />
                      {label === "Qty" && stockQtyText !== null && (
                        <div style={{ fontSize: 11, marginTop: 3, color: "#475569" }}>In stock: <b>{stockQtyText}</b></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div style={mStyles.tabsRow}>
                {["particulars", "docs", "change_log"].map((t) => (
                  <button key={t} className={`btn ${activeTab === t ? "btn-secondary" : "btn-light"}`}
                    onClick={() => setActiveTab(t)} style={{ fontSize: 13, padding: "4px 16px" }}>
                    {t === "change_log" ? "Change Log" : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === "particulars" && (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table style={mStyles.table}>
                      <thead>
                        <tr>
                          {viewBaseCols.map((h) => <th key={h} style={mStyles.th}>{h}</th>)}
                          <th colSpan={sampleLabels.length} style={{ ...mStyles.th, textAlign: "center", background: "#f0f4ff", borderLeft: "2px solid #c7d7ff" }}>
                            Sample
                          </th>
                          <th style={mStyles.th}>Status</th>
                          <th style={mStyles.th}>Remarks</th>
                        </tr>
                        <tr>
                          {viewBaseCols.map((h) => (
                            <th key={h} style={{ ...mStyles.th, paddingTop: 2, paddingBottom: 4, fontSize: 10, opacity: 0 }}>—</th>
                          ))}
                          {sampleLabels.map((n, ni) => (
                            <th key={`${n}-${ni}`} style={{
                              ...mStyles.th, textAlign: "center", background: "#f0f4ff",
                              fontSize: 11, paddingTop: 2, paddingBottom: 6,
                              borderLeft: ni === 0 ? "2px solid #c7d7ff" : undefined,
                            }}>
                              {n}
                            </th>
                          ))}
                          <th style={{ ...mStyles.th, paddingTop: 2, paddingBottom: 4, fontSize: 10, opacity: 0 }}>—</th>
                          <th style={{ ...mStyles.th, paddingTop: 2, paddingBottom: 4, fontSize: 10, opacity: 0 }}>—</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((r, idx) => {
                          const i      = detail.rows.indexOf(r);
                          const sv     = sampleValues[i] || { values: [] };
                          // Only rows whose process has Sample Requirement = "Yes"
                          // use Sample 2+; everything else is Sample 1 only.
                          const singleSample = r.sample_requirement !== "Yes";
                          const values = singleSample ? (sv.values || []).slice(0, 1) : (sv.values || []);
                          const pass   = anySamplePasses(r.value_type, r.standard_value, values);
                          const hasAny = values.some((v) => v);

                          const readonlyInput = (val) => {
                            const color = sampleMatchColor(r.value_type, r.standard_value, val);
                            return (
                              <input readOnly
                                style={{
                                  ...mStyles.inlineInput,
                                  background:  color === "pass" ? "#f0fdf4" : color === "fail" ? "#fff5f5" : "#f8fafc",
                                  borderColor: color === "pass" ? "#86efac" : color === "fail" ? "#fca5a5" : "#e2e8f0",
                                  cursor: "not-allowed",
                                }}
                                value={val || ""} placeholder="—"
                              />
                            );
                          };

                          return (
                            <tr key={i}>
                              {!isRawMaterial && (
                                <>
                                  <td style={mStyles.td}>{r.process_name || "—"}</td>
                                  <td style={mStyles.td}>{r.sub_process_name || "—"}</td>
                                </>
                              )}
                              <td style={mStyles.td}>{r.characteristic || "—"}</td>
                              <td style={mStyles.td}>{r.test_type || "—"}</td>
                              <td style={mStyles.td}>{r.uom || "—"}</td>
                              <td style={mStyles.td}>{r.value_type || "—"}</td>
                              <td style={mStyles.td}>{r.standard_value || "—"}</td>
                              {sampleLabels.map((_, si) => (
                                <td key={si} style={{ ...mStyles.td, borderLeft: si === 0 ? "2px solid #c7d7ff" : undefined, background: "#f8faff" }}>
                                  {singleSample && si !== 0 ? (
                                    <input readOnly disabled value="" placeholder="N/A"
                                      title="Sample Requirement is not Yes for this process — only Sample 1 applies"
                                      style={{ ...mStyles.inlineInput, background: "#f1f5f9", color: "#94a3b8", cursor: "not-allowed" }}
                                    />
                                  ) : readonlyInput(values[si])}
                                </td>
                              ))}
                              <td style={{ ...mStyles.td, textAlign: "center" }}>
                                {hasAny ? (
                                  pass
                                    ? <span style={mStyles.passIcon}>✓</span>
                                    : <span style={mStyles.failIcon}>✗</span>
                                ) : (
                                  <span style={{ color: "#cbd5e1", fontSize: 18 }}>—</span>
                                )}
                              </td>
                              <td style={mStyles.td}>{sv.remarks || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                </>
              )}

              {activeTab === "docs" && (
                <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e2e8f0" }}>
                  <div style={{ marginBottom: 16, padding: 15, border: "1px solid #d1d5db", borderRadius: 8, background: "#f8fafc" }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#000", marginBottom: 12 }}>
                      {detail.header.inspection_number}_{isRawMaterial ? "RawMaterialInspectionReport" : "InspectionReport"}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setShowReportModal(true)} style={mStyles.docActionBtn}>View</button>
                      <button style={mStyles.docActionBtn} onClick={() => {
                        setShowReportModal(true);
                        setTimeout(() => {
                          const el = document.querySelector(".inspection-report-content");
                          if (!el) { alert("Report not loaded yet"); setShowReportModal(false); return; }
                          html2pdf().set({
                            margin: 5,
                            filename: `${detail.header.inspection_number}_${isRawMaterial ? "RawMaterialInspectionReport" : "InspectionReport"}.pdf`,
                            html2canvas: { scale: 2, useCORS: true },
                            jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
                          }).from(el).save().then(() => setShowReportModal(false));
                        }, 3000);
                      }}>Download</button>
                      <button style={mStyles.docActionBtn} onClick={() => {
                        setShowReportModal(true);
                        setTimeout(() => {
                          const iframe = document.querySelector('iframe[title="Inspection Report"]');
                          if (!iframe?.contentWindow) { alert("Report not loaded"); setShowReportModal(false); return; }
                          iframe.contentWindow.focus(); iframe.contentWindow.print();
                          setTimeout(() => setShowReportModal(false), 1000);
                        }, 3000);
                      }}>Print</button>
                    </div>
                  </div>

                  {showReportModal && (
                    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={(e) => { if (e.target === e.currentTarget) setShowReportModal(false); }}>
                      <div style={{ width: "90%", height: "90%", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>
                            {detail.header.inspection_number}_{isRawMaterial ? "RawMaterialInspectionReport" : "InspectionReport"}
                          </span>
                          <button onClick={() => setShowReportModal(false)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" }}>✕</button>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
                          {/* ✅ FIX 2: encode inspectionNo before passing to report components */}
                          {isRawMaterial
                            ? <RawMaterialInspectionReportView
                                inspectionNo={safeInspNo(detail.header.inspection_number)}
                                stockId={item.id}
                                testType={item.test_type}
                                loggedInUser={(() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return u.name || u.username || (u.email ? String(u.email).split("@")[0] : ""); } catch { return ""; } })()}
                              />
                            : <InspectionReportView
                                inspectionNo={safeInspNo(detail.header.inspection_number)}
                                stockId={item.id}
                                testType={item.test_type}
                                loggedInUser={(() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return u.name || u.username || (u.email ? String(u.email).split("@")[0] : ""); } catch { return ""; } })()}
                              />
                          }
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: 16 }}>
                    <label htmlFor="qualityDocInputView" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8, padding: "18px 12px", cursor: "pointer", background: "#f8fafc" }}>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>Click to attach documents</span>
                      <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>PDF, Word, Excel, Images, etc.</span>
                      <input id="qualityDocInputView" type="file" ref={viewDocInputRef} multiple style={{ display: "none" }}
                        onChange={async (e) => {
                          const files    = Array.from(e.target.files); e.target.value = "";
                          if (!files.length) return;
                          const targetId = detail.header.inspection_number || "DRAFT";
                          const fd       = new FormData();
                          files.forEach((f) => fd.append("docs", f));
                          try {
                            const res    = await fetch(`${MASTER_API}/quality-configuration/upload-docs/${targetId}`,{ headers: authHeaders(), method: "POST", body: fd });
                            if (!res.ok) throw new Error(await res.text());
                            const result = await res.json();
                            const saved  = result.files.map((f) => ({
                              name: f.originalname, filename: f.filename,
                              size: f.size, type: f.mimetype,
                              uploadedAt: `${formatDateDMY(new Date())}, ${new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`, inspectionId: targetId,
                            }));
                            setViewDocFiles((prev) => [...prev, ...saved]);
                            toast.success(saved.length + " file(s) uploaded successfully");
                          } catch (err) { toast.error("File upload failed: " + err.message); }
                        }} />
                    </label>
                  </div>

                  {viewDocFiles.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8", fontSize: 13 }}>No documents attached yet.</div>
                  ) : (
                    <table style={mStyles.table}>
                      <thead>
                        <tr>{["#", "File Name", "Type", "Size", "Uploaded At", "Actions"].map((h) => <th key={h} style={mStyles.th}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {viewDocFiles.map((doc, idx) => (
                          <tr key={idx}>
                            <td style={mStyles.td}>{idx + 1}</td>
                            <td style={mStyles.td}>{doc.name}</td>
                            <td style={mStyles.td}>{doc.type || "—"}</td>
                            <td style={mStyles.td}>{doc.size > 1024 * 1024 ? (doc.size / (1024 * 1024)).toFixed(2) + " MB" : (doc.size / 1024).toFixed(1) + " KB"}</td>
                            <td style={mStyles.td}>{doc.uploadedAt || "—"}</td>
                            <td style={mStyles.td}>
                              <div style={{ display: "flex", gap: 6 }}>
                                <button onClick={() => setViewingDoc(doc)} style={mStyles.smallBtn}>View</button>
                                <button style={mStyles.smallBtn} onClick={() => {
                                  const a = document.createElement("a");
                                  a.href = `${MASTER_API}/quality-configuration/download-doc/${doc.inspectionId}/${doc.filename}`;
                                  a.download = doc.name;
                                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                }}>Download</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {viewingDoc && (
                    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={(e) => { if (e.target === e.currentTarget) setViewingDoc(null); }}>
                      <div style={{ width: "90%", height: "90%", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{viewingDoc.name}</span>
                          <button onClick={() => setViewingDoc(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
                        </div>
                        <iframe
                          src={`${MASTER_API}/quality-configuration/download-doc/${viewingDoc.inspectionId}/${viewingDoc.filename}`}
                          title={viewingDoc.name} width="100%" style={{ flex: 1, border: "none" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "change_log" && (
                <div style={{ overflowX: "auto" }}>
                  <table style={mStyles.table}>
                    <thead>
                      <tr>
                        <th style={mStyles.th}>Date & Time</th>
                        <th style={mStyles.th}>User</th>
                        <th style={mStyles.th}>Action</th>
                        <th style={mStyles.th}>Field</th>
                        <th style={mStyles.th}>Old Value</th>
                        <th style={mStyles.th}>New Value</th>
                        <th style={mStyles.th}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogs.length > 0 ? (
                        changeLogs.map((log, idx) => {
                          const dateVal = log.created_at || log.updated_at;
                          return (
                            <tr key={idx}>
                              <td style={mStyles.td}>
                                {(() => {
                                  const d = new Date(dateVal);
                                  if (isNaN(d.getTime())) return "";
                                  const dd = String(d.getDate()).padStart(2, "0");
                                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                                  const yyyy = d.getFullYear();
                                  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                                  return `${dd}/${mm}/${yyyy}, ${time}`;
                                })()}
                              </td>
                              <td style={mStyles.td}>{log.created_by || log.updated_by || "System"}</td>
                              <td style={mStyles.td}>{log.action_type || "Update"}</td>
                              <td style={mStyles.td}>
                                {log.field_name
                                  ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  : '-'}
                              </td>
                              <td style={{ ...mStyles.td, maxWidth: 150 }}>{log.old_value || "-"}</td>
                              <td style={{ ...mStyles.td, maxWidth: 150 }}>{log.new_value || "-"}</td>
                              <td style={{ ...mStyles.td, maxWidth: 200, wordWrap: "break-word" }}>{log.comment || "-"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ ...mStyles.td, textAlign: "center", padding: 20 }}>
                            No changes recorded
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────
function EditModal({ item, onClose, onSaved, moduleId = "QM_INSPECTION" }) {
  const { isFieldVisible } = useFieldPermissions(moduleId);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [saving, setSaving]             = useState(false);
  const [activeTab, setActiveTab]       = useState("particulars");
  const [rows, setRows]                 = useState([]);
  //eslint-disable-next-line
  const [mappingId, setMappingId]       = useState(null);
  const [testTypes, setTestTypes]       = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDrumAssignModal, setShowDrumAssignModal] = useState(false);
  const [drumAssigned, setDrumAssigned] = useState(false);
  const [checkingDrum, setCheckingDrum] = useState(true);
  const [showSampleConfigModal, setShowSampleConfigModal] = useState(false);
  const [copiedStdIdx, setCopiedStdIdx] = useState(null);
  const [sampleLabels, setSampleLabels] = useState(DEFAULT_SAMPLE_LABELS);
  const [wiNo, setWiNo]                 = useState("");
  const [refNo, setRefNo]               = useState("");
  const [testedBy, setTestedBy]         = useState("");
  const [witnessedBy, setWitnessedBy]   = useState("");
  const [witnessedDesignation, setWitnessedDesignation] = useState("");
  // Quantity actually being inspected right now. Defaults to the full
  // available qty on this stock row, but can be lowered to inspect only
  // part of it — the remaining qty stays behind as its own Pending line.
  const [inspectQty, setInspectQty]     = useState("");
  // Cut Piece — optional qty cut from the batch for testing. Only shown for
  // test types other than Routine Test. Deducted from stock qty on save.
  const [cutPieceQty, setCutPieceQty]   = useState("");
  const [changeLogs, setChangeLogs]     = useState([]);
  const [header, setHeader]             = useState({
    inspection_number: "", product_type: "", inspection_type: "",
    category: "", sub_category: "", qa_category: "", cable_type: "", product_code: "",
  });

  // Default the inspect-qty field to this stock row's full available qty
  // whenever a (new) item is loaded into the modal.
  useEffect(() => {
    setInspectQty(formatQty(item?.stock_qty ?? item?.qty) ?? "");
    setCutPieceQty("");
  }, [item]);

  // Check whether a drum has already been assigned for this stock (batch).
  // Only relevant when this test type requires drum assignment.
  useEffect(() => {
    const itemNeedsAssign =
      item?.requires_drum === "Yes" ||
      (item?.product_type || "").toLowerCase() === "finished goods";
    if (!itemNeedsAssign || !item?.id) {
      setCheckingDrum(false);
      return;
    }
    setCheckingDrum(true);
    axios.get(`${QUALITY_INSP_API}/drum-assignment/${item.id}`, { headers: authHeaders() })
      .then((res) => {
        const d = res.data || {};
        setDrumAssigned(!!(d && (d.drum_no || d.drum_type || d.assigned_date)));
      })
      .catch(() => setDrumAssigned(false))
      .finally(() => setCheckingDrum(false));
  }, [item?.id, item?.requires_drum, item?.product_type]);

  // Change Log — same pattern as PurchaseOrder.js's fetchLogs(poId), just
  // pointed at the Quality Inspection endpoint and keyed by stockId (item.id).
  const fetchLogs = async (stockId) => {
    try {
      const res = await axios.get(`${QUALITY_INSP_API}/logs/${stockId}`, { headers: authHeaders() });
      if (res.data.success) {
        setChangeLogs(res.data.logs);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

  useEffect(() => {
    if (item?.id) fetchLogs(item.id);
  }, [item?.id]);

  useEffect(() => {
    const inspNo = item?.format_number;
    if (!inspNo) { setLoading(false); return; }
    Promise.all([
      // ✅ FIX 3: encode inspNo + pass productCode so we get only this product's characteristics
      axios.get(`${INSPECTION_API}/detail/${safeInspNo(inspNo)}`, { headers: authHeaders(), params: { productCode: item.product_code, testType: item.test_type },}),
      axios.get(`${MASTER_API}/configuration/dynamic/test-type`, { headers: authHeaders() }),
      axios.get(`${QUALITY_INSP_API}/batch-actuals/${item.id}`, { headers: authHeaders(), params: { testType: item.test_type } }),
    ]).then(([detailRes, ttRes, avRes]) => {
      const data = detailRes.data || [];
      setTestTypes(ttRes.data || []);
      if (data.length > 0) {
        const h = data[0];
        setMappingId(h.mapping_id);
        setHeader({
          inspection_number: h.inspection_number || "",
          product_type:      h.product_type      || "",
          inspection_type:   h.inspection_type   || "",
          category:          h.category          || "",
          sub_category:      h.sub_category      || "",
          qa_category:       h.qa_category       || "",
          cable_type:        h.cable_type        || "",
          product_code:      h.product_code      || "",
        });

        const avPayload = avRes.data || {};
        const avList    = Array.isArray(avPayload) ? avPayload : (avPayload.actuals || []);
        setWiNo(avPayload.wi_no  || "");
        setRefNo(avPayload.ref_no || "");
        setTestedBy(avPayload.tested_by || "");
        setWitnessedBy(avPayload.witnessed_by || "");
        setWitnessedDesignation(avPayload.witnessed_designation || "");
        const labels = Array.isArray(avPayload.sample_labels) && avPayload.sample_labels.length > 0
          ? avPayload.sample_labels
          : DEFAULT_SAMPLE_LABELS;

        const avMap = {};
        avList.forEach((av) => {
          const values = Array.isArray(av.sample_values) && av.sample_values.length > 0
            ? av.sample_values
            : [av.sample1 || "", av.sample2 || "", av.sample3 || ""];
          avMap[av.characteristic_id] = {
            values,
            manualResult: av.manual_result ? av.manual_result.toLowerCase() : "",
            remarks: av.remarks || "",
          };
        });

        // Only show parameters that actually have a Standard Value
        // configured — rows with no standard value are skipped entirely.
        // Applies to every test type now: Routine Test, Type Test,
        // Customer Inspection, Store Check, Raw Material Inspection, etc.
        const isRoutineTest = (item.test_type || h.inspection_type || "").toLowerCase() === "routine test";
        const filteredData = data.filter(
          (r) => r.standard_value !== null && r.standard_value !== undefined && String(r.standard_value).trim() !== ""
        );

        // Sample columns are whatever was configured via the Sample No
        // popup for this batch (variable count/labels) for:
        //   - Routine Test (Finished Goods), OR
        //   - any Raw Material test type (Raw Material Inspection, Store
        //     Check, etc.) — Raw Material batches now flow-forward their
        //     Sample No/labels across test types too, so the label picked
        //     for e.g. "Store Check" must carry through to "Raw Material
        //     Inspection" instead of being discarded here.
        // Finished Goods' Type Test / Customer Inspection / others: still
        // always exactly the fixed 3 default sample columns — not
        // configurable, unchanged from before.
        const isRawMaterialItem = (item?.product_type || h.product_type || "").toLowerCase() === "raw material";
        const effectiveLabels = (isRoutineTest || isRawMaterialItem) ? labels : DEFAULT_SAMPLE_LABELS;
        setSampleLabels(effectiveLabels);

        setRows(applyFormulaRows(filteredData.map((r) => {
          const saved  = avMap[r.id] || {};
          const values = saved.values || [];
          // Pad/truncate to the current label count so every row always has
          // exactly one input per configured sample column.
          // Only an explicit "Yes" (Process Configuration → Sample Requirement,
          // e.g. Conductor / Insulation) may use Sample 2+. Every other row
          // is locked to Sample 1, so any stale later-sample values are
          // dropped here instead of silently affecting the Pass/Fail result.
          const rowSampleReq = r.sample_requirement === "Yes" ? "Yes" : "No";
          const samples = effectiveLabels.map((_, i) =>
            (rowSampleReq === "No" && i > 0) ? "" : (values[i] || "")
          );
          const rowObj = {
            id:             r.id,
            // Tick-box: only ticked rows appear in the form / get validated / saved.
            // Rows that already have saved actuals (re-opened inspection) start ticked.
            selected:       !!avMap[r.id],
            processName:    r.process_name     || "",
            subProcessName: r.sub_process_name || "",
            characteristic: r.characteristic   || "",
            testType:       r.test_type        || "",
            uom:            r.uom              || "",
            type:           r.value_type       || "",
            standardValue:  r.standard_value   || "",
            variable:       r.variable         || "",
            formula:        r.formula          || "",
            isFormula:      !!r.is_formula,
            // Only an explicit "Yes" (set in Process Configuration for this
            // process) unlocks multiple sample columns for this
            // characteristic. Blank/unset — same as an explicit "No" —
            // stays locked to Sample 1 only, even if the batch's overall
            // Sample No config has been widened to more columns.
            sampleRequirement: rowSampleReq,
            samples,
            manualResult:   saved.manualResult || "",
            remarks:        saved.remarks      || "",
          };
          if (!rowObj.manualResult) {
            rowObj.manualResult = computeAutoResult(rowObj);
          }
          return rowObj;
        })));
      } else {
        setError(`No data found for inspection number: ${inspNo}`);
      }
    })
    .catch((e) => setError(e.response?.data?.message || `API error ${e.response?.status}`))
    .finally(() => setLoading(false));
  }, [item]);
//eslint-disable-next-line
  const handleRowChange = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      const isTypeField = ["type", "standardValue"].includes(field);
      // Status is always auto-calculated — no manual override to check for.
      if (isTypeField) {
        updated.manualResult = computeAutoResult(updated);
      }
      return updated;
    }));
  };

  // Copies a row's Std Value to the clipboard AND pastes it into that row's
  // Sample (Actual Value) cells. Respects the same rules as manual entry:
  // formula rows are skipped, and Sample Requirement = "No"
  // rows only get Sample 1.
  const handleCopyStdToSamples = (idx) => {
    const row = rows[idx];
    if (!row) return;
    const std = String(row.standardValue ?? "").trim();
    if (!std) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(std).catch(() => {});
    }
    if (!row.isFormula) {
      setRows((prev) => applyFormulaRows(prev.map((r, i) => {
        if (i !== idx) return r;
        const count = r.sampleRequirement === "No" ? 1 : sampleLabels.length;
        const samples = [...(r.samples || [])];
        for (let k = 0; k < count; k++) samples[k] = std;
        // Pasting into an unticked row ticks it so the value is still saved.
        const updated = { ...r, samples, selected: true };
        updated.manualResult = computeAutoResult(updated);
        return updated;
      })));
    }
    setCopiedStdIdx(idx);
    setTimeout(() => setCopiedStdIdx((c) => (c === idx ? null : c)), 1200);
  };

  // sampleIdx: which sample column (0-based) — count/labels are dynamic per
  // batch, set via the "Sample No" popup, so this replaces the old fixed
  // handleRowChange(idx, "sample1"/"sample2"/"sample3", value) calls.
  const handleSampleChange = (idx, sampleIdx, value) => {
    setRows((prev) => applyFormulaRows(prev.map((r, i) => {
      if (i !== idx) return r;
      if (r.isFormula) return r;   // calculated automatically — not editable
      // Sample Requirement = "No" → Sample 1 (index 0) is the only column
      // that can be written for this row; ignore edits elsewhere (inputs
      // are also disabled in the UI — this just guards against stray
      // programmatic calls).
      if (r.sampleRequirement === "No" && sampleIdx !== 0) return r;
      const samples = [...(r.samples || [])];
      samples[sampleIdx] = value;
      // Entering a value in an unticked row ticks it so it is still saved.
      const updated = { ...r, samples, selected: r.selected || String(value).trim() !== "" };
      // Status is always auto-calculated from the entered sample values —
      // no manual Pass/Fail selection anymore, so this always recomputes.
      updated.manualResult = computeAutoResult(updated);
      return updated;
    })));
  };

  // Called after the Sample No popup saves new labels — re-syncs every
  // row's samples array to the new length (keeps existing values where the
  // index still exists, blanks out newly-added columns).
  const handleSampleConfigSaved = (newLabels) => {
    setSampleLabels(newLabels);
    setRows((prev) => applyFormulaRows(prev.map((r) => {
      const samples = newLabels.map((_, i) => (r.samples || [])[i] || "");
      const updated = { ...r, samples };
      updated.manualResult = computeAutoResult(updated);
      return updated;
    })));
    setShowSampleConfigModal(false);
  };

  const handleRowSelect = (idx, checked) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, selected: checked } : r)));
  };
  const handleSelectAll = (checked) => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const handleRemarksChange = (idx, value) => {
    setRows((prev) => prev.map((r, i) =>
      i === idx ? { ...r, remarks: value } : r
    ));
  };

  const isRawMaterial   = (header.product_type || "").toLowerCase() === "raw material";
  const isFinishedGoods = (header.product_type || "").toLowerCase() === "finished goods";
  // Assign (drum) must be completed before Accept/Reject for Finished Goods,
  // and also whenever the stock row itself flags requires_drum === "Yes".
  const needsAssign     = isFinishedGoods || item?.requires_drum === "Yes";
  const assignBlocking  = needsAssign && !drumAssigned;
  const editBaseCols  = isRawMaterial
    ? ["Parameters", "Test Type", "UOM", "Type", "Std Value"]
    : ["Process", "Sub Process", "Parameters", "Test Type", "UOM", "Type", "Std Value"];

  // Only ticked rows count toward the form, validation, Pass/Fail summary and save.
  const activeRows     = rows.filter((r) => r.selected);
  const allSelected    = rows.length > 0 && activeRows.length === rows.length;
  const allRowsDecided = activeRows.length > 0 && activeRows.every((r) => r.manualResult === "pass" || r.manualResult === "fail");
  const allMatch       = allRowsDecided && activeRows.every((r) => r.manualResult === "pass");

  // Limit = qty on the stock table row (falls back to list qty if not sent).
  const availableQty = Number(item?.stock_qty ?? item?.qty);
  const stockQtyText = (item?.stock_qty !== undefined && item?.stock_qty !== null && item?.stock_qty !== "")
    ? formatQty(item.stock_qty) : null;
  const enteredInspectQty  = Number(inspectQty);
  const inspectQtyExceeds  = !Number.isNaN(availableQty) && enteredInspectQty > availableQty;

  const validateRowsForSave = () => {
    if (!item?.id) {
      toast.error("Cannot save: stock record ID is missing.");
      return false;
    }
    if (rows.length === 0) {
      toast.warn("No inspection rows to save.");
      return false;
    }
    if (activeRows.length === 0) {
      toast.warn("Please tick at least one parameter to include in the inspection.");
      return false;
    }
    const undecided = activeRows.filter((r) => !r.manualResult);
    if (undecided.length > 0) {
      toast.warn(`Please enter sample values for all ${undecided.length} row(s) — Pass/Fail is calculated automatically once a value is entered.`);
      return false;
    }
    const qtyNum = Number(inspectQty);
    if (!inspectQty || Number.isNaN(qtyNum) || qtyNum <= 0) {
      toast.warn("Please enter a valid quantity to inspect.");
      return false;
    }
    if (!Number.isNaN(availableQty) && qtyNum > availableQty) {
      toast.warn(`Quantity to inspect can't exceed the available quantity (${availableQty}).`);
      return false;
    }
    if (cutPieceQty !== "" && cutPieceQty !== null && cutPieceQty !== undefined) {
      const cutNum = Number(cutPieceQty);
      if (Number.isNaN(cutNum) || cutNum <= 0) {
        toast.warn("Cut Piece qty must be a positive number.");
        return false;
      }
      if (!Number.isNaN(availableQty) && cutNum > availableQty) {
        toast.warn(`Cut Piece qty can't exceed the available quantity (${availableQty}).`);
        return false;
      }
    }
    return true;
  };

  // Accept → go straight into the existing match/mismatch → certificate flow.
  const handleAcceptClick = () => {
    if (assignBlocking) {
      toast.warn("Please complete Assign before Accept.");
      return;
    }
    if (!validateRowsForSave()) return;
    setConfirmState({ allMatch });
  };

  // Reject → stock row is marked fully Rejected (no certificate at all).
  const handleRejectClick = () => {
    if (assignBlocking) {
      toast.warn("Please complete Assign before Reject.");
      return;
    }
    if (!validateRowsForSave()) return;
    doSave("Rejected", false);
  };

  const doSave = async (forceStatus, generateCert) => {
    setConfirmState(null);
    setSaving(true);
    try {
      const withId = rows.filter((r) => r.selected && r.id);
      if (withId.length === 0) {
        toast.warn("No ticked characteristics with IDs found. Cannot save.");
        setSaving(false);
        return;
      }
      const currentUserEmail = (() => {
        try { return JSON.parse(localStorage.getItem("user") || "{}").email || ""; } catch { return ""; }
      })();
      await axios.post(`${QUALITY_INSP_API}/save`, {
          stockId: item.id,
          forceStatus,
          wiNo,
          refNo,
          testedBy,
          witnessedBy,
          witnessedDesignation,
          inspectedBy: currentUserEmail,
          inspectQty: Number(inspectQty),
          testType: item.test_type,
          cutPieceQty: (cutPieceQty !== "" && cutPieceQty !== null && cutPieceQty !== undefined)
            ? Number(cutPieceQty)
            : undefined,
          characteristics: withId.map((r) => ({
            id:             r.id,
            sample_values:  (r.sampleRequirement === "No")
              ? (r.samples || []).map((v, i) => (i === 0 ? v : ""))
              : (r.samples || []),
            standard_value: r.standardValue,
            manual_result:  r.manualResult
              ? (r.manualResult === "pass" ? "Pass" : "Fail")
              : undefined,
            remarks:        r.remarks || null,
          })),
        }, { headers: authHeaders() });

      fetchLogs(item.id);

      if (generateCert) {
        setShowReportModal(true);
        toast.success("Inspection saved! Generating test certificate…", { autoClose: 4000 });
      } else {
        toast.success("Inspection saved successfully!");
        onSaved && onSaved();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Sample (Actual Value) columns and the Sample No popup only apply to
  // Routine Test — Type Test / Customer Inspection / others use a single
  // manual Pass/Fail Status instead of per-sample entries.
  //eslint-disable-next-line
  const isRoutineTestType = (item?.test_type || header.inspection_type || "").toLowerCase() === "routine test";
  // Witnessed By / Designation only apply to Customer Inspection — other
  // test types (Routine Test, Type Test, etc.) don't collect a witness.
  //eslint-disable-next-line
  const isCustomerInspection = (item?.test_type || header.inspection_type || "").toLowerCase() === "customer inspection";

  const mainFields = Object.entries({
    "Inspection Number":    header.inspection_number,
    "Test Type":            item?.test_type || header.inspection_type,
    "Product Code":         header.product_code,
    "Product Description": item?.product_description || header.product_description,
    "Doc No":               isRawMaterial ? item?.grn_number : item?.production_order_number,
    "Supplier":             isRawMaterial ? item?.supplier_name : displayCustomerName(item?.customer_name),
    "Batch No":             item?.batch_number,
    "Qty":                  formatQty(item?.qty),
    "UOM":                  item?.uom,
    ...(!isRawMaterial ? {
      "Sales Order No":  item?.sales_order,
      "Date of Testing": item?.date_of_testing ? formatDateDMY(item.date_of_testing) : "",
    } : {}),
  }).filter(([, v]) => v !== undefined && v !== null && v !== "");

  const headerFields = Object.entries({
    "Category":          header.category,
    "Sub Category":      header.sub_category,
    "QA Category":       header.qa_category,
    "Cable Type":        header.cable_type,
  }).filter(([, v]) => v);

  const typeHintBadge = (type, std) => {
    if (!std) return null;
    let hint = "";
    switch ((type || "").trim()) {
      case "Min":   hint = `≥ ${std}`; break;
      case "Max":   hint = `≤ ${std}`; break;
      case "Range": hint = `${std}`;   break;
      default:      return null;
    }
    return (
      <div style={{ fontSize: 10, color: "#6366f1", marginTop: 2, fontWeight: 600, letterSpacing: 0.2 }}>
        {hint}
      </div>
    );
  };

  return (
    <>
      {confirmState && (
        <ConfirmDialog
          allMatch={confirmState.allMatch}
          onGenerateCert={() => doSave("Inspected", true)}
          onSkip={() => doSave(confirmState.allMatch ? "Inspected" : "Pending", false)}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* ✅ FIX 4: encode inspectionNo before passing to report components */}
      {showReportModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowReportModal(false); onSaved && onSaved(); onClose(); } }}>
          <div style={{ width: "92%", height: "92%", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                {header.inspection_number}_{isRawMaterial ? "RawMaterialInspectionReport" : "InspectionReport"}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={mStyles.smallBtn} onClick={() => {
                  const el = document.querySelector(".inspection-report-content");
                  if (!el) return;
                  html2pdf().set({
                    margin: 5,
                    filename: `${header.inspection_number}_Certificate.pdf`,
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
                  }).from(el).save();
                }}>Download PDF</button>
                <button onClick={() => { setShowReportModal(false); onSaved && onSaved(); onClose(); }}
                  style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
              {isRawMaterial
                ? <RawMaterialInspectionReportView
                    inspectionNo={safeInspNo(header.inspection_number)}
                    stockId={item.id}
                    testType={item.test_type}
                    loggedInUser={(() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return u.name || u.username || (u.email ? String(u.email).split("@")[0] : ""); } catch { return ""; } })()}
                  />
                : <InspectionReportView
                    inspectionNo={safeInspNo(header.inspection_number)}
                    stockId={item.id}
                    testType={item.test_type}
                    loggedInUser={(() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return u.name || u.username || (u.email ? String(u.email).split("@")[0] : ""); } catch { return ""; } })()}
                  />
              }
            </div>
          </div>
        </div>
      )}

      <div style={mStyles.backdrop}>
        <div style={mStyles.modal}>
          <div style={mStyles.modalHeader}>
            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: "#0f172a" }}>Inspection Parameters</h4>
            <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={mStyles.modalBody}>
            {loading ? (
              <div style={mStyles.centreMsg}>Loading…</div>
            ) : error ? (
              <div style={{ ...mStyles.centreMsg, color: "#dc2626" }}>⚠️ {error}</div>
            ) : (
              <>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label style={mStyles.fieldLabel}>Product Type</label>
                    <input type="text" className="form-control" value={header.product_type || ""} readOnly
                      style={{ height: 38, fontSize: 13, background: "#f8fafc" }} />
                  </div>
                  <div className="col-md-3">
                    <label style={mStyles.fieldLabel}>W.I. No</label>
                    <input type="text" className="form-control" placeholder="Enter W.I. No"
                      value={wiNo} onChange={(e) => setWiNo(e.target.value)}
                      style={{ height: 38, fontSize: 13 }} />
                  </div>
                  <div className="col-md-3">
                    <label style={mStyles.fieldLabel}>Ref</label>
                    <input type="text" className="form-control" placeholder="Enter Ref"
                      value={refNo} onChange={(e) => setRefNo(e.target.value)}
                      style={{ height: 38, fontSize: 13 }} />
                  </div>
                  <div className="col-md-3">
                    <label style={mStyles.fieldLabel}>Tested By</label>
                    <input type="text" className="form-control" placeholder="Enter Tested By"
                      value={testedBy} onChange={(e) => setTestedBy(e.target.value)}
                      style={{ height: 38, fontSize: 13 }} />
                  </div>
                  {isCustomerInspection && (
                    <>
                      <div className="col-md-3">
                        <label style={mStyles.fieldLabel}>Witnessed By</label>
                        <input type="text" className="form-control" placeholder="Enter Witnessed By"
                          value={witnessedBy} onChange={(e) => setWitnessedBy(e.target.value)}
                          style={{ height: 38, fontSize: 13 }} />
                      </div>
                      <div className="col-md-3">
                        <label style={mStyles.fieldLabel}>Designation</label>
                        <input type="text" className="form-control" placeholder="Enter Designation"
                          value={witnessedDesignation} onChange={(e) => setWitnessedDesignation(e.target.value)}
                          style={{ height: 38, fontSize: 13 }} />
                      </div>
                    </>
                  )}
                </div>
                <div style={mStyles.sectionBox}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(140px, 1fr))", gap: 12 }}>
                    {[...mainFields, ...headerFields].map(([label, value], i) => (
                      <React.Fragment key={i}>
                        <div style={{ flex: "1 1 0", minWidth: 100 }}>
                          <label style={mStyles.miniLabel}>{label.toUpperCase()}</label>
                          <input type="text" value={QI_LABEL_TO_FIELD[label] && !isFieldVisible(QI_LABEL_TO_FIELD[label]) ? QI_MASK : value} readOnly className="form-control form-control-sm"
                            style={{ height: 32, fontSize: 13, background: "#fff" }} />
                          {label === "Qty" && stockQtyText !== null && (
                            <div style={{ fontSize: 11, marginTop: 3, color: "#475569" }}>In stock: <b>{stockQtyText}</b></div>
                          )}
                        </div>
                        {label === "Qty" && (
                          <div style={{ flex: "1 1 0", minWidth: 100 }}>
                            <label style={mStyles.miniLabel}>QUANTITY TO INSPECT</label>
                            <input
                              type="number"
                              min="0"
                              max={Number.isNaN(availableQty) ? undefined : availableQty}
                              value={inspectQty}
                              onChange={(e) => setInspectQty(e.target.value)}
                              className="form-control form-control-sm"
                              style={{ height: 32, fontSize: 13, background: "#fff",
                                ...(inspectQtyExceeds ? { borderColor: "#dc2626", boxShadow: "0 0 0 1px #dc2626" } : {}) }}
                            />
                            {inspectQty !== "" && !Number.isNaN(enteredInspectQty) && enteredInspectQty > 0 && !Number.isNaN(availableQty) && (
                              inspectQtyExceeds ? (
                                <div style={{ fontSize: 11, marginTop: 3, fontWeight: 600, color: "#dc2626" }}>
                                  Exceeds stock qty by {formatQty(enteredInspectQty - availableQty)} — max {formatQty(availableQty)}
                                </div>
                              ) : (
                                <div style={{ fontSize: 11, marginTop: 3, fontWeight: 600, color: "#16a34a" }}>
                                  Inspecting {formatQty(enteredInspectQty)} · Remaining {formatQty(availableQty - enteredInspectQty)}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    <div style={{ flex: "1 1 0", minWidth: 100 }}>
                      <label style={mStyles.miniLabel}>SAMPLE NO</label>
                      <button
                        type="button"
                        onClick={() => setShowSampleConfigModal(true)}
                        title="Set number of samples and their labels for this batch"
                        className="form-control form-control-sm"
                        style={{
                          height: 32, fontSize: 13, textAlign: "left", cursor: "pointer",
                          background: "#fff", color: "#0f172a",
                        }}
                      >
                        {sampleLabels.length}
                      </button>
                    </div>
                    {!isRoutineTestType && (
                      <>
                        <div style={{ flex: "1 1 0", minWidth: 100 }}>
                          <label style={mStyles.miniLabel}>CUT PIECE</label>
                          <input
                            type="number"
                            min="0"
                            max={Number.isNaN(availableQty) ? undefined : availableQty}
                            value={cutPieceQty}
                            placeholder="Optional"
                            onChange={(e) => setCutPieceQty(e.target.value)}
                            className="form-control form-control-sm"
                            style={{ height: 32, fontSize: 13, background: "#fff" }}
                          />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 100 }}>
                          <label style={mStyles.miniLabel}>UOM</label>
                          <input type="text" value={item?.uom || ""} readOnly
                            className="form-control form-control-sm"
                            style={{ height: 32, fontSize: 13, background: "#fff" }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {!Number.isNaN(availableQty) && Number(inspectQty) > 0 && Number(inspectQty) < availableQty && (
                  <div style={{ fontSize: 12, color: "#b45309", marginTop: -8, marginBottom: 8 }}>
                    Partial inspection: {inspectQty} of {availableQty} will move to {"'"}Inspected{"'"} /
                    the entered result, and the remaining {availableQty - Number(inspectQty)} will stay
                    Pending as a separate line item.
                  </div>
                )}
                <div style={mStyles.tabsRow}>
                  {["particulars", "docs", "change_log"].map((t) => (
                    <button key={t} className={`btn ${activeTab === t ? "btn-secondary" : "btn-light"}`}
                      onClick={() => setActiveTab(t)} style={{ fontSize: 13, padding: "4px 16px" }}>
                      {t === "change_log" ? "Change Log" : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>

                {activeTab === "particulars" && (() => {
                  // Deduplicate rows by id to prevent same characteristic showing twice
                  const seenIds = new Set();
                  const uniqueRows = rows.reduce((acc, r, i) => {
                    const key = r.id ? String(r.id) : `${r.characteristic}__${r.testType}__${i}`;
                    if (!seenIds.has(key)) { seenIds.add(key); acc.push({ r, i }); }
                    return acc;
                  }, []);

                  // sIdx: index into the row's dynamic samples array (count/labels
                  // come from sampleLabels, set via the "Sample No" popup).
                  const sampleCell = (r, i, sIdx) => {
                    const val = (r.samples || [])[sIdx] || "";
                    // Sample Requirement = "No" → only Sample 1 (index 0) is
                    // fillable for this row; the rest are locked blank.
                    const disabledForRow = (r.sampleRequirement === "No") && sIdx !== 0;
                    if (disabledForRow) {
                      return (
                        <input
                          style={{
                            ...mStyles.inlineInput,
                            background: "#f1f5f9",
                            color: "#94a3b8",
                            cursor: "not-allowed",
                          }}
                          value=""
                          placeholder="N/A"
                          readOnly
                          disabled
                          title="Sample Requirement is set to No for this process — only Sample 1 applies"
                        />
                      );
                    }
                    const color = sampleMatchColor(r.type, r.standardValue, val);
                    if (r.isFormula) {
                      return (
                        <input
                          style={{
                            ...mStyles.inlineInput,
                            background: "#eef2ff",
                            fontFamily: "monospace",
                            fontWeight: 600,
                            cursor: "not-allowed",
                            borderColor: color === "pass" ? "#16a34a" : color === "fail" ? "#f87171" : "#c7d2fe",
                          }}
                          value={val}
                          placeholder="Auto"
                          readOnly
                          title={`Calculated automatically: ${r.formula}`}
                        />
                      );
                    }
                    return (
                      <input
                        style={{
                          ...mStyles.inlineInput,
                          borderColor: color === "pass" ? "#16a34a" : color === "fail" ? "#f87171" : "#cbd5e1",
                          background:  color === "pass" ? "#f0fdf4"  : color === "fail" ? "#fff5f5"  : "#fff",
                        }}
                        value={val}
                        placeholder="Enter…"
                        onChange={(e) => handleSampleChange(i, sIdx, e.target.value)}
                      />
                    );
                  };

                  const renderRow = ({ r, i }, rowIdx) => {
                    const manualValue = r.manualResult || "";
                    return (
                      <tr key={i} style={{ background: rowIdx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        <td style={{ ...mStyles.td, textAlign: "center", width: 36 }}>
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => handleRowSelect(i, e.target.checked)}
                            title="Tick to include this parameter in the inspection"
                          />
                        </td>
                        {!isRawMaterial && (
                          <>
                            <td style={mStyles.td}>
                              <input style={{ ...mStyles.inlineInput, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                                value={r.processName} readOnly disabled />
                            </td>
                            <td style={mStyles.td}>
                              <input style={{ ...mStyles.inlineInput, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                                value={r.subProcessName} readOnly disabled />
                            </td>
                          </>
                        )}
                        <td style={mStyles.td}>
                          <input style={{ ...mStyles.inlineInput, minWidth: 180, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                            title={r.characteristic}
                            value={r.characteristic} readOnly disabled />
                        </td>
                        <td style={mStyles.td}>
                          <select className="form-select form-select-sm" style={{ minWidth: 160, fontSize: 12, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                            title={r.testType}
                            value={r.testType} disabled>
                            <option value="">Select</option>
                            {testTypes.map((tt, idx) => (
                              <option key={`${tt.name}-${idx}`} value={tt.name}>{tt.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={mStyles.td}>
                          <input style={{ ...mStyles.inlineInput, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                            value={r.uom} readOnly disabled />
                        </td>
                        <td style={mStyles.td}>
                          <select className="form-select form-select-sm" style={{ minWidth: 100, fontSize: 12, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                            value={r.type} disabled>
                            <option value="">Select</option>
                            {TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </td>
                        <td style={mStyles.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <input style={{ ...mStyles.inlineInput, minWidth: 130, background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                              title={r.standardValue}
                              value={r.standardValue} readOnly disabled />
                            {String(r.standardValue ?? "").trim() !== "" && (
                              <button
                                type="button"
                                onClick={() => handleCopyStdToSamples(i)}
                                title="Copy Std Value and paste it into the samples"
                                style={{
                                  flex: "0 0 auto", width: 28, height: 28, padding: 0,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  border: "1px solid #cbd5e1", borderRadius: 4, background: "#fff",
                                  color: copiedStdIdx === i ? "#16a34a" : "#475569", cursor: "pointer",
                                }}
                              >
                                {copiedStdIdx === i ? <Check size={14} /> : <Copy size={14} />}
                              </button>
                            )}
                          </div>
                          {typeHintBadge(r.type, r.standardValue)}
                        </td>
                        {sampleLabels.map((_, sIdx) => (
                          <td key={sIdx} style={{ ...mStyles.td, borderLeft: sIdx === 0 ? "2px solid #c7d7ff" : undefined }}>
                            {sampleCell(r, i, sIdx)}
                          </td>
                        ))}
                        <td style={{ ...mStyles.td, textAlign: "center" }}>
                          <div
                            title="Auto-calculated from the entered sample values — not manually editable"
                            style={{
                              fontSize: 12, fontWeight: 700, minWidth: 110, height: 30,
                              borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center",
                              border: manualValue === "pass" ? "1px solid #86efac" : manualValue === "fail" ? "1px solid #fca5a5" : "1px solid #cbd5e1",
                              background: manualValue === "pass" ? "#f0fdf4" : manualValue === "fail" ? "#fff5f5" : "#fff",
                              color: manualValue === "pass" ? "#16a34a" : manualValue === "fail" ? "#dc2626" : "#94a3b8",
                            }}
                          >
                            {manualValue === "pass" ? "✓ Pass" : manualValue === "fail" ? "✗ Fail" : "—"}
                          </div>
                        </td>
                        <td style={mStyles.td}>
                          <input
                            type="text"
                            placeholder="Enter remarks…"
                            value={r.remarks || ""}
                            onChange={(e) => handleRemarksChange(i, e.target.value)}
                            style={{ ...mStyles.inlineInput, minWidth: 140 }}
                          />
                        </td>
                      </tr>
                    );
                  };

                  const tableHead = (
                    <thead>
                      <tr>
                        <th style={{ ...mStyles.th, width: 36, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            title="Tick / untick all parameters"
                          />
                        </th>
                        {editBaseCols.map((h) => <th key={h} style={mStyles.th}>{h}</th>)}
                        <th colSpan={sampleLabels.length} style={{ ...mStyles.th, textAlign: "center", background: "#f0f4ff", borderLeft: "2px solid #c7d7ff" }}>
                          Sample (Actual Value)
                        </th>
                        <th style={mStyles.th}>Status</th>
                        <th style={mStyles.th}>Remarks</th>
                      </tr>
                      <tr>
                        <th style={{ ...mStyles.th, paddingTop: 2, paddingBottom: 4, background: "#f8fafc", fontSize: 10, color: "transparent" }}>.</th>
                        {editBaseCols.map((h) => (
                          <th key={h} style={{ ...mStyles.th, paddingTop: 2, paddingBottom: 4, background: "#f8fafc", fontSize: 10, color: "transparent" }}>.</th>
                        ))}
                        {sampleLabels.map((n, ni) => (
                          <th key={`${n}-${ni}`} style={{
                            ...mStyles.th, textAlign: "center", background: "#f0f4ff",
                            fontSize: 12, paddingTop: 2, paddingBottom: 6,
                            borderLeft: ni === 0 ? "2px solid #c7d7ff" : undefined,
                          }}>{n}</th>
                        ))}
                        <th style={{ ...mStyles.th, paddingTop: 2, background: "#f8fafc", fontSize: 10, color: "transparent" }}>.</th>
                        <th style={{ ...mStyles.th, paddingTop: 2, background: "#f8fafc", fontSize: 10, color: "transparent" }}>.</th>
                      </tr>
                    </thead>
                  );

                  return (
                    <div style={{ overflowX: "auto" }}>
                      <table style={mStyles.table}>
                        {tableHead}
                        <tbody>{uniqueRows.map((item, idx) => renderRow(item, idx))}</tbody>
                      </table>
                    </div>
                  );
                })()}

                {activeTab === "docs" && (
                  <div style={{ padding: "12px 0", color: "#64748b", fontSize: 13 }}>No documents attached.</div>
                )}

                {activeTab === "change_log" && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={mStyles.table}>
                      <thead>
                        <tr>
                          <th style={mStyles.th}>Date & Time</th>
                          <th style={mStyles.th}>User</th>
                          <th style={mStyles.th}>Action</th>
                          <th style={mStyles.th}>Field</th>
                          <th style={mStyles.th}>Old Value</th>
                          <th style={mStyles.th}>New Value</th>
                          <th style={mStyles.th}>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changeLogs.length > 0 ? (
                          changeLogs.map((log, idx) => {
                            const dateVal = log.created_at || log.updated_at;
                            return (
                              <tr key={idx}>
                                <td style={mStyles.td}>
                                  {(() => {
                                    const d = new Date(dateVal);
                                    if (isNaN(d.getTime())) return "";
                                    const dd = String(d.getDate()).padStart(2, "0");
                                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                                    const yyyy = d.getFullYear();
                                    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                                    return `${dd}/${mm}/${yyyy}, ${time}`;
                                  })()}
                                </td>
                                <td style={mStyles.td}>{log.created_by || log.updated_by || "System"}</td>
                                <td style={mStyles.td}>{log.action_type || "Update"}</td>
                                <td style={mStyles.td}>
                                  {log.field_name
                                    ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                    : '-'}
                                </td>
                                <td style={{ ...mStyles.td, maxWidth: 150 }}>{log.old_value || "-"}</td>
                                <td style={{ ...mStyles.td, maxWidth: 150 }}>{log.new_value || "-"}</td>
                                <td style={{ ...mStyles.td, maxWidth: 200, wordWrap: "break-word" }}>{log.comment || "-"}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" style={{ ...mStyles.td, textAlign: "center", padding: 20 }}>
                              No changes recorded
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {showDrumAssignModal && (
                  <DrumAssignModal
                    stockId={item.id}
                    onClose={() => setShowDrumAssignModal(false)}
                    onSaved={() => {
                      setDrumAssigned(true);
                      setShowDrumAssignModal(false);
                    }}
                  />
                )}

                {showSampleConfigModal && (
                  <SampleConfigModal
                    stockId={item.id}
                    testType={item.test_type}
                    currentLabels={sampleLabels}
                    onClose={() => setShowSampleConfigModal(false)}
                    onSaved={handleSampleConfigSaved}
                  />
                )}
              </>
            )}
          </div>

          <div style={mStyles.modalFooter}>
            {allRowsDecided && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: allMatch ? "#f0fdf4" : "#fff5f5",
                border: `1px solid ${allMatch ? "#16a34a" : "#f87171"}`,
                borderRadius: 10, padding: "10px 14px",
                flex: 1, marginRight: 16,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: allMatch ? "#16a34a" : "#dc2626",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <span style={{ color: "#fff", fontSize: 15, fontWeight: 700, lineHeight: 1 }}>
                    {allMatch ? "✓" : "✗"}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: allMatch ? "#15803d" : "#dc2626", marginBottom: 2 }}>
                    {allMatch ? "All parameters match" : "Some parameters don't match"}
                  </div>
                  <div style={{ fontSize: 12, color: allMatch ? "#166534" : "#b91c1c", lineHeight: 1.5 }}>
                    {allMatch
                      ? "All characteristics marked as Pass."
                      : "One or more characteristics marked as Fail."}
                  </div>
                </div>
              </div>
            )}
            {!allRowsDecided && rows.length > 0 && (
              <div style={{ flex: 1, marginRight: 16, fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                Please enter sample values for each row before saving — Pass/Fail is calculated automatically.
              </div>
            )}
            {needsAssign && (
              <button
                onClick={() => setShowDrumAssignModal(true)}
                disabled={checkingDrum}
                title={drumAssigned ? "Click to update drum details" : "Assign drum before saving"}
                style={{
                  ...mStyles.saveBtn,
                  background: drumAssigned ? "#16a34a" : "#000",
                  marginRight: 10,
                }}
              >
                {checkingDrum ? "Checking…" : drumAssigned ? "✓ Assigned" : "Assign"}
              </button>
            )}
            <button
              onClick={handleAcceptClick}
              disabled={saving || checkingDrum || assignBlocking}
              title={assignBlocking ? "Please complete Assign first" : ""}
              style={{
                ...mStyles.saveBtn,
                background: "#16a34a",
                marginRight: 10,
                opacity: (saving || checkingDrum || assignBlocking) ? 0.5 : 1,
                cursor: (saving || checkingDrum || assignBlocking) ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Accept"}
            </button>
            <button
              onClick={handleRejectClick}
              disabled={saving || checkingDrum || assignBlocking}
              title={assignBlocking ? "Please complete Assign first" : ""}
              style={{
                ...mStyles.saveBtn,
                background: "#dc2626",
                opacity: (saving || checkingDrum || assignBlocking) ? 0.5 : 1,
                cursor: (saving || checkingDrum || assignBlocking) ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── DRUM ASSIGN MODAL ──────────────────────────────────────────────────────
// One assignment per stock (batch). Saving here updates the same row every
// time, and since the backend's getStockHeader() reads from this same table,
// every test-type report for this stock (Routine Test, Type Test, Customer
// Inspection, etc.) picks up the same values automatically — no re-entry.
function DrumAssignModal({ stockId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    drum_type: "",
    drum_material: "",
    top_m: "",
    bottom_m: "",
    length_m: "",
    temp: "",
    drum_no: "",
    assigned_date: todayISO(),
  });

  useEffect(() => {
    if (!stockId) return;
    axios.get(`${QUALITY_INSP_API}/drum-assignment/${stockId}`, { headers: authHeaders() })
      .then((res) => {
        const d = res.data || {};
        setForm({
          drum_type:     d.drum_type || "",
          drum_material: d.drum_material || "",
          top_m:         d.top_m ?? "",
          bottom_m:      d.bottom_m ?? "",
          length_m:      d.length_m ?? "",
          temp:          d.temp || "",
          drum_no:       d.drum_no || "",
          assigned_date: d.assigned_date ? String(d.assigned_date).slice(0, 10) : todayISO(),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [stockId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.assigned_date) {
      toast.error("Please enter a valid date (dd-mm-yyyy)");
      return;
    }
    setSaving(true);
    try {
      const loggedInUser = (() => {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          return u.name || u.username || (u.email ? String(u.email).split("@")[0] : "");
        } catch { return ""; }
      })();

      await axios.post(`${QUALITY_INSP_API}/drum-assignment`, {
        stockId,
        ...form,
        assigned_by: loggedInUser,
      }, { headers: authHeaders() });

      toast.success("Drum assigned successfully");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save drum assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={mStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...mStyles.modal, width: 520, maxWidth: "92vw", height: "auto", maxHeight: "90vh" }}>
        <div style={mStyles.modalHeader}>
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: "#0f172a" }}>Assign Drum</h4>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ ...mStyles.modalBody, display: "flex", flexDirection: "column", gap: 14 }}>
          {loading ? (
            <div style={mStyles.centreMsg}>Loading…</div>
          ) : (
            <>
              {/* <div>
                <label style={mStyles.fieldLabel}>Drum Type</label>
                <input style={mStyles.inlineInput} value={form.drum_type} onChange={set("drum_type")} placeholder="e.g. Wooden Drum" />
              </div> */}

              <div>
                <label style={mStyles.fieldLabel}>Wooden / Steel</label>
                <select style={mStyles.inlineInput} value={form.drum_material} onChange={set("drum_material")}>
                  <option value="">-- Select --</option>
                  <option value="Wooden">Wooden</option>
                  <option value="Steel">Steel</option>
                  <option value="Coil">Coil</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={mStyles.fieldLabel}>Top (M)</label>
                  <input type="number" style={mStyles.inlineInput} value={form.top_m} onChange={set("top_m")} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={mStyles.fieldLabel}>Bottom (M)</label>
                  <input type="number" style={mStyles.inlineInput} value={form.bottom_m} onChange={set("bottom_m")} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={mStyles.fieldLabel}>Length (M)</label>
                  <input type="number" style={mStyles.inlineInput} value={form.length_m} onChange={set("length_m")} />
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={mStyles.fieldLabel}>Temp</label>
                  <input style={mStyles.inlineInput} value={form.temp} onChange={set("temp")} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={mStyles.fieldLabel}>Drum No</label>
                  <input style={mStyles.inlineInput} value={form.drum_no} onChange={set("drum_no")} />
                </div>
              </div>

              <div>
                <label style={mStyles.fieldLabel}>Date</label>
                <DateInputDMY style={mStyles.inlineInput} value={form.assigned_date} onChange={(v) => setForm((f) => ({ ...f, assigned_date: v }))} />
              </div>
            </>
          )}
        </div>

        <div style={mStyles.modalFooter}>
          <button onClick={handleSave} disabled={saving || loading} style={mStyles.saveBtn}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SAMPLE NO / LABELS MODAL ───────────────────────────────────────────────
// One config per stock (batch), same pattern as DrumAssignModal. The user
// types how many samples this batch needs, then names each one — those
// names become the "Sample. 1 / Sample. 2 / ..." column headings, replacing
// the old fixed 3-column layout.
function SampleConfigModal({ stockId, testType, currentLabels, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [count, setCount]     = useState((currentLabels && currentLabels.length) || 3);
  const [labels, setLabels]   = useState(currentLabels && currentLabels.length ? currentLabels : DEFAULT_SAMPLE_LABELS);

  useEffect(() => {
    if (!stockId) { setLoading(false); return; }
    // Scoped by test type now — Routine/Type/Customer Inspection each keep
    // their own sample count/labels for the same stock_id instead of
    // silently sharing (and overwriting) one config between them.
    axios.get(`${QUALITY_INSP_API}/sample-config/${stockId}`, {
      params: { testType: testType || "" },
      headers: authHeaders(),
    })
      .then((res) => {
        const d = res.data || {};
        const savedLabels = Array.isArray(d.sample_labels) && d.sample_labels.length > 0
          ? d.sample_labels
          : DEFAULT_SAMPLE_LABELS;
        setCount(savedLabels.length);
        setLabels(savedLabels);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [stockId, testType]);

  // Changing the count resizes the labels array — existing labels are kept
  // where the index still exists, new slots default to "Sample. N".
  const handleCountChange = (e) => {
    const raw = e.target.value;
    setCount(raw);
    const n = parseInt(raw, 10);
    if (!n || n <= 0) return;
    const capped = Math.min(n, 20);
    setLabels((prev) => Array.from({ length: capped }, (_, i) => prev[i] || `Sample. ${i + 1}`));
  };

  const handleLabelChange = (idx, value) => {
    setLabels((prev) => prev.map((l, i) => (i === idx ? value : l)));
  };

  const handleSave = async () => {
    const n = parseInt(count, 10);
    if (!n || n <= 0) {
      toast.error("Please enter a valid sample number (1 or more)");
      return;
    }
    if (n > 20) {
      toast.error("Sample number cannot exceed 20");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${QUALITY_INSP_API}/sample-config`, {
        stockId,
        testType: testType || "",
        sample_labels: labels,
      }, { headers: authHeaders() });
      toast.success("Sample configuration saved");
      onSaved && onSaved(res.data?.sample_labels || labels);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save sample configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={mStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...mStyles.modal, width: 480, maxWidth: "92vw", height: "auto", maxHeight: "90vh" }}>
        <div style={mStyles.modalHeader}>
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: "#0f172a" }}>Sample No</h4>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ ...mStyles.modalBody, display: "flex", flexDirection: "column", gap: 14 }}>
          {loading ? (
            <div style={mStyles.centreMsg}>Loading…</div>
          ) : (
            <>
              <div>
                <label style={mStyles.fieldLabel}>Sample No</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  style={mStyles.inlineInput}
                  value={count}
                  onChange={handleCountChange}
                  placeholder="e.g. 5"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={mStyles.fieldLabel}>Label for each sample</label>
                {labels.map((label, idx) => (
                  <input
                    key={idx}
                    style={mStyles.inlineInput}
                    value={label}
                    onChange={(e) => handleLabelChange(idx, e.target.value)}
                    placeholder={`Sample. ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div style={mStyles.modalFooter}>
          <button onClick={handleSave} disabled={saving || loading} style={mStyles.saveBtn}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const USER_MANUAL_SCOPE = {
    moduleName: "Quality",
    process: "Quality Mangement",
    subProcess: "Quality Inspection"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
// ── Screen scopes ─────────────────────────────────────────────────────
// One engine, three tiles. The backend is a single module/table; each tile
// just narrows which stock rows (product_type + test_type) this screen shows.
//   FG    → Finished Goods rows
//   RM    → Raw Material rows EXCEPT Store Check
//   STORE → Store Check rows (a Raw Material test type)
//   ALL   → no narrowing (legacy / default when no scope is passed)
const _pt = (r) => String(r?.product_type || "").trim().toLowerCase();
const _tt = (v) => String(v || "").trim().toLowerCase();
const isStoreCheckType = (v) => _tt(v) === "store check";

const QUALITY_SCOPES = {
  ALL:   { title: "Quality Inspection",        subtitle: "Manage Quality Inspections",
           productType: "",               rowOk: () => true,                                              testTypeOk: () => true },
  FG:    { title: "Quality Inspection - FG",   subtitle: "Manage Finished Goods Quality Inspections",
           productType: "Finished Goods", rowOk: (r) => _pt(r) === "finished goods",                      testTypeOk: () => true },
  RM:    { title: "Quality Inspection - RM",   subtitle: "Manage Raw Material Quality Inspections",
           productType: "Raw Material",   rowOk: (r) => _pt(r) === "raw material" && !isStoreCheckType(r.test_type), testTypeOk: (n) => !isStoreCheckType(n) },
  STORE: { title: "Store Check",               subtitle: "Manage Store Checks for received Raw Material",
           productType: "Raw Material",   rowOk: (r) => isStoreCheckType(r.test_type),                     testTypeOk: (n) => isStoreCheckType(n) },
};

function Quality({ scope = "ALL" }) {
  const scopeCfg = QUALITY_SCOPES[scope] || QUALITY_SCOPES.ALL;
  const moduleId = qiModuleId(scope);
  const qualityInspectionPermission = getQualityInspectionPermission(moduleId);
  const { isFieldVisible } = useFieldPermissions(moduleId);

  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);

  const emptyFormData = {
    productCode: "", batchNo: "", productType: "", inspectionType: "", docNo: "", salesOrderNo: "", supplier: "",
    construction: "", code: "", cableType: "", category: "", noOfCores: "", color: "",
    nominalCrossSection: "", referenceOfStandard: "", voltage: "",
  };
  const [formData, setFormData]     = useState(emptyFormData);
  const [masterData, setMasterData] = useState({
    products: [], productTypes: [], allInspectionTypes: [],
    constructions: [], codes: [], cableTypes: [], categories: [], cores: [],
    colours: [], nominalCrossSections: [], referenceOfStandards: [], voltages: [],
  });
  // Cascading options for the Construction…Voltage filter row, narrowed to
  // only the combinations that actually exist in real product data — same
  // /product-master/filter-options endpoint and pattern Product.js uses for
  // its own filters. Starts out equal to the full masterData lists (set once
  // fetchDropdowns() resolves) and then re-narrows every time any one of
  // these filters changes, via fetchFilterOptions() below.
  const [filterOptions, setFilterOptions] = useState({
    construction: [], code: [], cableType: [], category: [], noOfCores: [],
    color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: [],
  });
  // productCode (lowercased) → full productdetails row, used to resolve
  // Construction/Cable Type/Category/etc for stock rows that don't already
  // carry those columns directly.
  const [productAttrByCode, setProductAttrByCode] = useState({});
  const [stockList, setStockList]   = useState([]);
  const [allStock, setAllStock]     = useState([]);
  const [viewItem, setViewItem]     = useState(null);
  const [editItem, setEditItem]     = useState(null);
  const [statusTab, setStatusTab]   = useState("active");
  const [selectedIds, setSelectedIds]         = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkSaving, setBulkSaving]           = useState(false);
  // ── Pagination (10 rows/page) — the list used to just get taller and
  // taller and rely on the page's own scrollbar; this caps each page at
  // PAGE_SIZE rows with Prev/Next controls below the table instead.
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  // sequenceLocks: { [stockId]: [{ inspection_test_type, sequence, completed, locked }] }
  const [sequenceLocks, setSequenceLocks] = useState({});

  // ── Top mirror scrollbar for the (wide) stock table ──
  // The table's own horizontal scrollbar sits below every row, so on a long
  // list you have to scroll all the way down just to find it. This adds a
  // thin, always-visible scrollbar right above the table header instead —
  // it's a same-width empty div whose horizontal scroll position is kept in
  // sync (both ways) with the real table container below it.
  const tableScrollRef  = React.useRef(null);
  const bottomScrollRef = React.useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  // Monotonic id for fetchFilterOptions() calls — lets us ignore a slow,
  // out-of-order response that would otherwise overwrite a newer one.
  const filterReqId = React.useRef(0);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const measure = () => setTableScrollWidth(el.scrollWidth);
    measure();
    // Re-measure on resize (column widths / sidebar collapse can change it).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stockList, statusTab]);

  // Only one visible scrollbar: the frozen bar pinned to the bottom of the
  // viewport. The table's own native scrollbar is hidden via CSS (below)
  // but still scrolls — kept in sync with the frozen bar in both directions.
  const syncScrollFromTable = (e) => {
    if (bottomScrollRef.current) bottomScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  const syncScrollFromBottom = (e) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  useEffect(() => {
    fetchDropdowns();
    fetchStockList();
  }, []);

  // Re-narrow the Construction…Voltage dropdown options every time one of
  // them changes, so e.g. picking a Cable Type only leaves the Categories
  // (etc.) that actually co-occur with it in real product data — same
  // cascading behaviour as the Product Master listing filters.
  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.construction,
    formData.code,
    formData.cableType,
    formData.category,
    formData.noOfCores,
    formData.color,
    formData.nominalCrossSection,
    formData.referenceOfStandard,
    formData.voltage,
  ]);

  useEffect(() => {
    let filtered = allStock.filter(scopeCfg.rowOk);
    if (formData.productType)
      filtered = filtered.filter((r) => (r.product_type || "").toLowerCase() === formData.productType.toLowerCase());
    if (formData.productCode)
      filtered = filtered.filter((r) => (r.product_code || "").toLowerCase() === formData.productCode.toLowerCase());
    if (formData.batchNo)
      filtered = filtered.filter((r) => (r.batch_number || "").toLowerCase().includes(formData.batchNo.toLowerCase()));
    if (formData.inspectionType)
      filtered = filtered.filter((r) => (r.test_type || "").toLowerCase() === formData.inspectionType.toLowerCase());
    if (formData.docNo)
      filtered = filtered.filter((r) => {
        // Raw Material rows use the GRN number as their doc no; Finished
        // Goods rows use the production order number (mirrors the "Doc No"
        // table column above).
        const doc = (r.product_type || "").toLowerCase() === "raw material"
          ? r.grn_number
          : r.production_order_number;
        return (doc || "").toLowerCase().includes(formData.docNo.toLowerCase());
      });
    if (formData.salesOrderNo)
      filtered = filtered.filter((r) => (r.sales_order || "").toLowerCase().includes(formData.salesOrderNo.toLowerCase()));
    if (formData.supplier)
      filtered = filtered.filter((r) => {
        // Raw Material rows show supplier_name; Finished Goods rows show the
        // customer name in the same "Supplier" table column, so match either.
        const supp = (r.product_type || "").toLowerCase() === "finished goods"
          ? displayCustomerName(r.customer_name)
          : r.supplier_name;
        return (supp || "").toLowerCase().includes(formData.supplier.toLowerCase());
      });
    // Product-attribute filters (Construction, Code, Cable Type, Category,
    // Cores, Color, Cross Section, Std. Ref, Voltage) — same fields/behaviour
    // as the Production Order listing filters, resolved per-row via product_code.
    ["construction", "code", "cableType", "category", "noOfCores", "color",
     "nominalCrossSection", "referenceOfStandard", "voltage"].forEach((attrKey) => {
      const filterVal = formData[attrKey];
      if (filterVal) {
        filtered = filtered.filter(
          (r) => getProductAttr(r, productAttrByCode, attrKey).toLowerCase() === filterVal.toLowerCase()
        );
      }
    });
    filtered = filtered.filter((r) => {
      const st = (r.status || "").toLowerCase();
      // "Returned To Vendor" means the batch already left — it belongs in
      // Inspected alongside Inspected/Rejected, not stuck in Active forever.
      const isResolved = st === "inspected" || st === "rejected" || st === "returned to vendor";
      return statusTab === "inspected" ? isResolved : !isResolved;
    });
    setStockList(filtered);
    setSelectedIds([]);
    setCurrentPage(1); // filters/tab changed — always land back on page 1
  }, [formData, allStock, statusTab, productAttrByCode, scope]);

  // Total pages for the current filtered list, and the slice of rows that
  // actually belong on the page currently being viewed.
  const totalPages = Math.max(1, Math.ceil(stockList.length / PAGE_SIZE));
  const pagedStockList = stockList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // If the list shrinks (e.g. an item moves tabs after being inspected) and
  // the current page no longer exists, fall back to the last valid page.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Pulls the narrowed option lists for the Construction…Voltage filter row
  // from the same /product-master/filter-options endpoint Product.js's
  // listing filters use — passing the currently-selected values so the
  // backend can return only combinations that exist together in real
  // product data (true cascading, not just clearing child selections).
  const fetchFilterOptions = async () => {
    const myId = ++filterReqId.current;
    try {
      const res = await axios.get(`${MASTER_API}/product-master/filter-options`, {
        headers: authHeaders(),
        params: {
          // NOTE: "construction" deliberately left out — Product.js's own
          // reference call to this same endpoint doesn't send it either
          // (only reads filterOptions.construction back from the response),
          // so sending it risked the backend rejecting/erroring on an
          // unexpected param and silently falling back to the seeded
          // master list, which is what was happening here.
          code:                 formData.code || "",
          cableType:            formData.cableType || "",
          category:             formData.category || "",
          noOfCores:            formData.noOfCores || "",
          color:                formData.color || "",
          nominalCrossSection:  formData.nominalCrossSection || "",
          referenceOfStandard:  formData.referenceOfStandard || "",
          voltage:              formData.voltage || "",
        },
      });
      if (myId !== filterReqId.current) return; // stale response — ignore
      const d = res.data || {};
      setFilterOptions((prev) => {
        const next = { ...prev };
        // Accept alternate key names the backend may use for Cores.
        const incoming = { ...d, noOfCores: d.noOfCores ?? d.cores ?? d.core };
        Object.keys(incoming).forEach((k) => {
          const v = incoming[k];
          // Don't let an empty/missing list wipe a previously good list
          // (e.g. the seeded master list) unless the user has actually
          // narrowed the filters and nothing matches.
          if (Array.isArray(v) && (v.length > 0 || !(prev[k] && prev[k].length))) next[k] = v;
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to fetch filter options", err);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [
        productsFull, productTypes, inspTestTypes,
        resCons, resCode, resCable, resCat, resCore, resCol, resCross, resRef, resVolt,
      ] = await Promise.all([
        // Pull the FULL product list (code + description) in one shot by
        // requesting a very high limit — this endpoint is paginated
        // (10/page by default) so a bare call without params only
        // returns page 1.
        axios.get(`${MASTER_API}/product-master/products`, { headers: authHeaders(), params: { page: 1, limit: 100000 }}),
        axios.get(`${MASTER_API}/configuration/product-types`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/inspection-test-type`, { headers: authHeaders() }),
        // Same product-attribute filter master-data endpoints used by the
        // Production Order listing page (Construction … Voltage).
        axios.get(`${MASTER_API}/configuration/construction`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/dynamic/code`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/cable-type`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/category`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/core`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/colour`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/dynamic/nominal-cross-section`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/dynamic/reference-of-standard`, { headers: authHeaders() }),
        axios.get(`${MASTER_API}/configuration/dynamic/voltage`, { headers: authHeaders() }),
      ]);

      const rawProducts = productsFull.data?.data || productsFull.data || [];
      const productsWithDesc = rawProducts
        .map((item) => {
          const code = (typeof item === "string") ? item : (item.productCode || item.product_code || item.name || "");
          const desc = (typeof item === "string") ? "" : (item.productDescription || item.product_description || "");
          return { productCode: code, productDescription: desc };
        })
        .filter((p) => p.productCode);

      // Build productCode → full row lookup (keeps every column returned,
      // e.g. construction/cableType/productCategory/noOfCores/color/
      // nominalCrossSection/referenceOfStandard/voltage) so stock rows can
      // resolve these attributes even if the stock list API doesn't join them.
      const attrMap = {};
      rawProducts.forEach((item) => {
        if (typeof item === "string") return;
        const code = item.productCode || item.product_code || "";
        if (code) attrMap[code.toLowerCase()] = item;
      });
      setProductAttrByCode(attrMap);

      setMasterData({
        products:              productsWithDesc,
        productTypes:          productTypes.data || [],
        allInspectionTypes:    inspTestTypes.data || [],   // [{ id, product_type, sequence, name }]
        constructions:         resCons.data  || [],
        codes:                 resCode.data  || [],
        cableTypes:            resCable.data || [],
        categories:            resCat.data   || [],
        cores:                 resCore.data  || [],
        colours:               resCol.data   || [],
        nominalCrossSections:  resCross.data || [],
        referenceOfStandards:  resRef.data   || [],
        voltages:              resVolt.data  || [],
      });

      // Seed filterOptions with the full unfiltered lists so the dropdowns
      // have something to show immediately; fetchFilterOptions() (triggered
      // right after, and again on every selection change) narrows these
      // down to real co-occurring combinations.
      // IMPORTANT: only seed keys that are still empty. fetchFilterOptions()
      // (fired on mount) may already have returned the narrowed lists by the
      // time this slower Promise.all resolves — overwriting them here would
      // bring back the raw master list (with duplicates/typos) after refresh.
      setFilterOptions((prev) => {
        const seed = {
          construction:         resCons.data  || [],
          code:                 resCode.data  || [],
          cableType:            resCable.data || [],
          category:             resCat.data   || [],
          noOfCores:            resCore.data  || [],
          color:                resCol.data   || [],
          nominalCrossSection:  resCross.data || [],
          referenceOfStandard:  resRef.data   || [],
          voltage:              resVolt.data  || [],
        };
        const next = { ...prev };
        Object.keys(seed).forEach((k) => {
          if (!prev[k] || prev[k].length === 0) next[k] = seed[k];
        });
        return next;
      });
    } catch (err) { console.error("fetchDropdowns error:", err); }
  };

  const fetchStockList = async () => {
    try {
      const res  = await axios.get(`${PURCHASE_API}/quality-inspection`, { headers: authHeaders() });
      const data = res.data || [];
      setAllStock(data);
      // Fetch sequence lock status for all active stock rows in parallel
      // (skip rows that are already resolved — Inspected, or gone back to
      // the vendor — a lock check on those is wasted work).
      const lockResults = await Promise.allSettled(
        data.filter(r => {
          const st = (r.status || "").toLowerCase();
          return st !== "inspected" && st !== "returned to vendor";
        }).map(r => axios.get(`${QUALITY_INSP_API}/sequence-lock/${r.id}`, { headers: authHeaders() }).then(lr => ({ id: r.id, locks: lr.data })))
      );
      const lockMap = {};
      lockResults.forEach(res => {
        if (res.status === "fulfilled") lockMap[res.value.id] = res.value.locks;
      });
      setSequenceLocks(lockMap);
    } catch (err) { console.error("fetchStockList error:", err); }
  };

  // A single stock (batch) can appear as multiple rows — one per test type
  // (Routine Test / Type Test / Customer Inspection) — all sharing the same
  // underlying stock id. Selection must be keyed per-ROW (id + test_type),
  // not just id, otherwise ticking one test type ticks all test types for
  // that batch/product code.
  const getRowKey = (item) => `${item.id}::${item.test_type || "none"}`;

  const toggleSelectOne = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey) ? prev.filter((x) => x !== rowKey) : [...prev, rowKey]
    );
  };

  const allOnPageSelected = pagedStockList.length > 0 && pagedStockList.every((r) => selectedIds.includes(getRowKey(r)));
  const toggleSelectAll   = () => {
    const pageKeys = pagedStockList.map((r) => getRowKey(r));
    setSelectedIds((prev) =>
      allOnPageSelected
        ? prev.filter((k) => !pageKeys.includes(k))
        : [...new Set([...prev, ...pageKeys])]
    );
  };

  const handleInspectClick = () => {
    if (selectedIds.length === 0) {
      toast.warn("Select at least one product to inspect.");
      return;
    }
    setShowBulkConfirm(true);
  };

 const submitBulkStatus = async (status) => {
  setBulkSaving(true);
  try {
    // Keep stockId + testType paired — don't collapse to bare stock ids,
    // since one stock row can carry multiple independent test types.
    const items = selectedIds
      .map((key) => {
        const row = stockList.find((r) => getRowKey(r) === key);
        if (!row) return null;
        return { stockId: row.id, testType: row.test_type };
      })
      .filter(Boolean);

    await axios.post(`${QUALITY_INSP_API}/bulk-inspect`, { items, status }, { headers: authHeaders() });
    toast.success(`${selectedIds.length} item(s) marked as ${status}`);
    setShowBulkConfirm(false);
    setSelectedIds([]);
    fetchStockList();
  } catch (err) {
    toast.error(err.response?.data?.message || `Failed to mark as ${status}`);
  } finally {
    setBulkSaving(false);
  }
};

  const confirmBulkInspect = () => submitBulkStatus("Inspected");
  const confirmBulkReject  = () => submitBulkStatus("Rejected");

  return (
    <Layout defaultSidebarOpen={false}>
      <div style={{ padding: "30px 40px", backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>

        <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ margin: "0 0 4px 0", color: "#0f172a", fontWeight: 700, fontSize: 28 }}>{scopeCfg.title}</h2>
            <p style={{ color: "#64748b", margin: 0, fontSize: 15 }}>{scopeCfg.subtitle}</p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {statusTab === "active" && qualityInspectionPermission.can_edit && (
              <button
                onClick={handleInspectClick}
                style={{
                  fontSize: 13, fontWeight: 600, padding: "8px 18px", borderRadius: 8,
                  border: "none", cursor: "pointer", color: "#fff",
                  background: selectedIds.length > 0 ? "#021843" : "#94a3b8",
                  display: "flex", alignItems: "center", gap: 6,
                }}
                title={selectedIds.length === 0 ? "Select products to inspect" : `Inspect ${selectedIds.length} selected`}
              >
                ✓ Inspect{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </button>
            )}

            <button
              onClick={() => {
                setManualExists(null);
                setManualFile(null);
                setShowManual(true);
                fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                  .then((res) => res.json())
                  .then((data) => {
                    const norm = (v) => String(v || "").trim().toLowerCase();
                    const match = (data?.data || []).find((m) =>
                      norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                      norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                      norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                    );
                    if (match && match.drive_link) {
                      setManualFile(match);
                      setManualExists(true);
                    } else {
                      setManualExists(false);
                    }
                  })
                  .catch(() => setManualExists(false));
              }}
              title="View User Manual"
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                color: "#3b82f6",
                fontSize: "14px",
                fontWeight: "600",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >
              <FileText size={18} />
              User Manual
            </button>
          </div>
        </div>

        {/* --- User Manual Popup Modal --- */}
        {showManual && (
          <div
            onClick={() => setShowManual(false)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
              alignItems: "center", justifyContent: "center", zIndex: 1100,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                width: "90vw",
                height: "97vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
              }}
            >
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
              }}>
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                  User Manual
                </h4>
                <button
                  onClick={() => setShowManual(false)}
                  style={{
                    border: "none", background: "#f1f5f9", borderRadius: "8px",
                    width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                    color: "#475569", lineHeight: 1,
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>

              {manualExists === null && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                  Checking for user manual...
                </div>
              )}

              {manualExists === false && (
                <div style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                }}>
                  <FileText size={40} color="#cbd5e1" />
                  <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                    User Manual Not Available
                  </div>
                  <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                  </div>
                </div>
              )}

              {manualExists === true && manualFile && (() => {
                const MANUAL_SCALE = 1.25;
                return (
                  <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    <iframe
                      src={manualFile.drive_link.replace("/view", "/preview")}
                      title={manualFile.file_name || "User Manual"}
                      allow="autoplay"
                      style={{
                        border: "none",
                        width: `${100 / MANUAL_SCALE}%`,
                        height: `${100 / MANUAL_SCALE}%`,
                        transform: `scale(${MANUAL_SCALE})`,
                        transformOrigin: "top left",
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div style={pgStyles.filterCard}>
          {scope === "ALL" && (
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label style={pgStyles.compactLabel}>Product Type</label>
              <Select styles={customSelectStyles} options={toOptions(masterData.productTypes)}
                value={formData.productType ? { value: formData.productType, label: formData.productType } : null}
                onChange={(s) => setFormData({ ...formData, productType: s?.value || "", inspectionType: "" })}
                isClearable placeholder="All Types" />
            </div>
          </div>
          )}

          {/* Row 2: Construction … Voltage — same fields, layout, and Select
              styling as the Production Order listing page's filter row */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12, marginBottom: 2 }}>
            {[
              { key: "construction",        opts: filterOptions.construction,        label: "Construction" },
              { key: "code",                opts: filterOptions.code,                label: "Code" },
              { key: "cableType",           opts: filterOptions.cableType,           label: "Cable Type" },
              { key: "category",            opts: filterOptions.category,            label: "Category" },
              { key: "noOfCores",           opts: filterOptions.noOfCores,           label: "Cores" },
              { key: "color",               opts: filterOptions.color,               label: "Color" },
              { key: "nominalCrossSection", opts: filterOptions.nominalCrossSection, label: "Cross Section" },
              { key: "referenceOfStandard", opts: filterOptions.referenceOfStandard, label: "Std. Ref" },
              { key: "voltage",             opts: filterOptions.voltage,             label: "Voltage" },
            ].map(({ key, opts, label }) => (
              <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                <Select
                  placeholder={label}
                  isClearable
                  options={toFilterOptions(opts)}
                  value={formData[key] ? { value: formData[key], label: formData[key] } : null}
                  onChange={(s) => setFormData({ ...formData, [key]: s?.value || "" })}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 32, height: 32,
                      fontSize: 12,
                      borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
                      borderRadius: 6,
                      boxShadow: "none",
                      cursor: "pointer",
                      "&:hover": { borderColor: "#94a3b8" },
                    }),
                    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
                    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                    indicatorsContainer: (base) => ({ ...base, height: 32 }),
                    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                    indicatorSeparator: () => ({ display: "none" }),
                    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
                    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base, fontSize: 12, padding: "6px 10px",
                      backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
                      color: state.isSelected ? "#fff" : "#334155",
                    }),
                  }}
                />
              </div>
            ))}
          </div>

          {/* Row 3: Inspection Type | Product Code | Batch No | Doc No | Sales Order | Supplier
              — flex-wrap (like Row 2) so every filter sits on one line and all of
              them apply to both Raw Material and Finished Goods rows. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
            <div style={{ flex: "1 1 0", minWidth: 140 }}>
              <label style={pgStyles.compactLabel}>Inspection Type</label>
              <Select styles={customSelectStyles}
                options={
                  ((formData.productType || scopeCfg.productType)
                    ? masterData.allInspectionTypes.filter(r =>
                        (r.product_type || "").toLowerCase() === (formData.productType || scopeCfg.productType).toLowerCase()
                      )
                    : masterData.allInspectionTypes
                  )
                  .filter(r => scopeCfg.testTypeOk(r.name))
                  .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                  .map(r => ({ value: r.name, label: `${r.name}` }))
                }
                value={formData.inspectionType ? { value: formData.inspectionType, label: formData.inspectionType } : null}
                onChange={(s) => setFormData({ ...formData, inspectionType: s?.value || "" })}
                isClearable
                placeholder={formData.productType ? "Select Inspection Type" : "All Types"}
              />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 140 }}>
              <label style={pgStyles.compactLabel}>Product Code</label>
              <Select styles={customSelectStyles}
                options={masterData.products.map((p) => ({
                  value: p.productCode,
                  label: p.productDescription ? `${p.productCode} - ${p.productDescription}` : p.productCode,
                }))}
                value={formData.productCode
                  ? {
                      value: formData.productCode,
                      label: (() => {
                        const match = masterData.products.find((p) => p.productCode === formData.productCode);
                        return match?.productDescription ? `${formData.productCode} - ${match.productDescription}` : formData.productCode;
                      })(),
                    }
                  : null}
                onChange={(s) => setFormData({ ...formData, productCode: s?.value || "" })}
                isClearable placeholder="All Codes" />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 110 }}>
              <label style={pgStyles.compactLabel}>Batch No</label>
              <input className="form-control" style={{ fontSize: 13, height: 32 }}
                placeholder="Search batch…" value={formData.batchNo}
                onChange={(e) => setFormData({ ...formData, batchNo: e.target.value })} />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 110 }}>
              <label style={pgStyles.compactLabel}>Doc No</label>
              <input className="form-control" style={{ fontSize: 13, height: 32 }}
                placeholder="Search doc no…" value={formData.docNo || ""}
                onChange={(e) => setFormData({ ...formData, docNo: e.target.value })} />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 110 }}>
              <label style={pgStyles.compactLabel}>Sales Order</label>
              <input className="form-control" style={{ fontSize: 13, height: 32 }}
                placeholder="Search sales order…" value={formData.salesOrderNo || ""}
                onChange={(e) => setFormData({ ...formData, salesOrderNo: e.target.value })} />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 110 }}>
              <label style={pgStyles.compactLabel}>Supplier</label>
              <input className="form-control" style={{ fontSize: 13, height: 32 }}
                placeholder="Search supplier…" value={formData.supplier || ""}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} />
            </div>
            <div style={{ flex: "0 0 auto", minWidth: 90 }}>
              {Object.entries(formData).some(([, v]) => v) && (
                <button
                  style={{ fontSize: 11, color: "#ef4444", background: "none", border: "1px solid #fca5a5", borderRadius: 4, padding: "6px 10px", cursor: "pointer", height: 32 }}
                  onClick={() => setFormData(emptyFormData)}>
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
          <StatusTabs value={statusTab} onChange={setStatusTab} />
        </div>

        {/* Hide the table's own native horizontal scrollbar — the frozen
            bottom mirror below is the ONLY scrollbar the user sees; the
            table itself still scrolls via scrollLeft sync. */}
        <style>{`
          .qi-hide-native-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
          .qi-hide-native-scrollbar::-webkit-scrollbar { display: none; height: 0; }
        `}</style>

        <div
          className="qi-hide-native-scrollbar"
          style={pgStyles.tableContainer}
          ref={tableScrollRef}
          onScroll={syncScrollFromTable}
        >
          <table style={{ width: "100%", minWidth: 1400, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {statusTab === "active" && (
                  <th style={{ ...pgStyles.erpTh, width: 36, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                      style={{ cursor: "pointer", width: 15, height: 15 }}
                      title="Select all"
                    />
                  </th>
                )}
                {["Product Type", "Doc No", "Sales Order No", "Supplier", "Product Code", "Product Description", "Batch No", "Qty", "UOM", "Inspection Type",
                  "Date of Testing", "Drum No",
                  ...(statusTab === "inspected" ? ["Status"] : []),
                  "Action"].map((h) => (
                  <th key={h} style={pgStyles.erpTh}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedStockList.length > 0 ? (
                pagedStockList.map((item, i) => (
                  <tr key={i}
                    style={{ background: selectedIds.includes(getRowKey(item)) ? "#eff6ff" : (i % 2 === 0 ? "#fff" : "#f8fafc") }}
                    onMouseEnter={(e) => { if (!selectedIds.includes(getRowKey(item))) e.currentTarget.style.background = "#f1f5f9"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = selectedIds.includes(getRowKey(item)) ? "#eff6ff" : (i % 2 === 0 ? "#fff" : "#f8fafc"); }}>
                    {statusTab === "active" && (
                      <td style={{ ...pgStyles.erpTd, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(getRowKey(item))}
                          onChange={() => toggleSelectOne(getRowKey(item))}
                          style={{ cursor: "pointer", width: 15, height: 15 }}
                        />
                      </td>
                    )}
                    <td style={pgStyles.erpTd}>{!isFieldVisible("product_type") ? <MaskedValue /> : <span style={pgStyles.badge}>{item.product_type || "—"}</span>}</td>
                    <td style={{ ...pgStyles.erpTd, fontFamily: "monospace", fontSize: 12 }}>
                      {!isFieldVisible("doc_no") ? <MaskedValue /> : (((item.product_type || "").toLowerCase() === "raw material"
                        ? item.grn_number
                        : item.production_order_number) || "—")}
                    </td>
                    <td style={pgStyles.erpTd}>
                      {!isFieldVisible("sales_order") ? <MaskedValue /> : ((item.product_type || "").toLowerCase() === "finished goods" ? (item.sales_order || "—") : "—")}
                    </td>
                    <td style={pgStyles.erpTd}>
                      {!isFieldVisible("supplier") ? <MaskedValue /> : (((item.product_type || "").toLowerCase() === "finished goods"
                        ? displayCustomerName(item.customer_name)
                        : item.supplier_name) || "—")}
                    </td>
                    <td style={{ ...pgStyles.erpTd, fontWeight: 600, color: "#0f172a" }}>{!isFieldVisible("product_code") ? <MaskedValue /> : (item.product_code || "—")}</td>
                    <td style={{ ...pgStyles.erpTd, maxWidth: 260 }}>{!isFieldVisible("product_description") ? <MaskedValue /> : (item.product_description || "—")}</td>
                    <td style={pgStyles.erpTd}>{!isFieldVisible("batch_number") ? <MaskedValue /> : (item.batch_number || "—")}</td>
                    <td style={{ ...pgStyles.erpTd, textAlign: "right" }}>{!isFieldVisible("qty") ? <MaskedValue /> : (item.qty !== undefined && item.qty !== null && item.qty !== "" ? formatQty(item.qty) : "—")}</td>
                    <td style={pgStyles.erpTd}>{!isFieldVisible("uom") ? <MaskedValue /> : (item.uom || "—")}</td>
                    <td style={pgStyles.erpTd}>{!isFieldVisible("test_type") ? <MaskedValue /> : (item.test_type || "—")}</td>
                    <td style={pgStyles.erpTd}>
                      {!isFieldVisible("date_of_testing") ? <MaskedValue /> : ((item.product_type || "").toLowerCase() === "finished goods"
                        ? (item.date_of_testing ? formatDateDMY(item.date_of_testing) : "—")
                        : "—")}
                    </td>
                    <td style={pgStyles.erpTd}>
                      {!isFieldVisible("drum_no") ? <MaskedValue /> : ((item.product_type || "").toLowerCase() === "finished goods" ? (item.drum_no || "—") : "—")}
                    </td>
                    {statusTab === "inspected" && (
                      <td style={pgStyles.erpTd}>
                        {(() => {
                          const statusLc = (item.status || "").toLowerCase();
                          const isRejected = statusLc === "rejected";
                          const isReturned = statusLc === "returned to vendor";
                          const label = isReturned ? "Returned To Vendor" : (isRejected ? "Rejected" : "Inspected");
                          const bg    = isReturned ? "#fef3c7" : (isRejected ? "#fee2e2" : "#dcfce7");
                          const color = isReturned ? "#b45309" : (isRejected ? "#dc2626" : "#16a34a");
                          return (
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12,
                              background: bg,
                              color:      color,
                              whiteSpace: "nowrap",
                            }}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td style={pgStyles.erpTd}>
                      {(() => {
                        const locks = sequenceLocks[item.id] || [];
                        // Find the lock entry matching this row's test_type
                        const thisLock = locks.find(l => l.inspection_test_type === item.test_type);
                        const isLocked = thisLock?.locked === true;
                        // Find what test type needs to be done first
                        const blockedBy = isLocked
                          ? locks.find(l => !l.completed && locks.indexOf(l) < locks.indexOf(thisLock))
                          : null;
                        const lockTooltip = isLocked
                          ? `Complete "${blockedBy?.inspection_test_type || "previous test"}" first (Seq ${blockedBy?.sequence ?? "?"})`
                          : undefined;
                        // Show completion badge for multi-test products —
                        // only on the Active tab. On the Inspected tab every
                        // row is already inspected, so the "✓ Done" badge is
                        // redundant/confusing there and is skipped.
                        const hasMultiple = statusTab !== "inspected" && locks.length > 1;
                        const isThisCompleted = thisLock?.completed === true;
                        return (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            {hasMultiple && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                                background: isThisCompleted ? "#dcfce7" : isLocked ? "#fef3c7" : "#dbeafe",
                                color: isThisCompleted ? "#16a34a" : isLocked ? "#b45309" : "#1d4ed8",
                                whiteSpace: "nowrap",
                              }}>
                                {isThisCompleted ? "✓ Done" : isLocked ? `🔒 Seq ${thisLock?.sequence}` : `Seq ${thisLock?.sequence}`}
                              </span>
                            )}
                            <button className="btn btn-outline-dark btn-sm" onClick={() => setViewItem(item)}
                              disabled={!item.format_number} title={!item.format_number ? "No format number" : "View"}
                              style={{ fontSize: 12, padding: "3px 12px" }}>View</button>
                            {qualityInspectionPermission.can_edit && (
                            <button
                              className="btn btn-sm"
                              onClick={() => {
                                if (isLocked) {
                                  toast.warn(
                                    `Complete "${blockedBy?.inspection_test_type || "previous test"}" (Sequence ${blockedBy?.sequence ?? "?"}) before proceeding to "${item.test_type}".`,
                                    { autoClose: 5000 }
                                  );
                                  return;
                                }
                                setEditItem(item);
                              }}
                              disabled={!item.format_number}
                              title={isLocked ? lockTooltip : !item.format_number ? "No format number" : "Edit"}
                              style={{
                                fontSize: 12, padding: "3px 12px",
                                background: isLocked ? "#f1f5f9" : "#021843",
                                color: isLocked ? "#94a3b8" : "#fff",
                                border: isLocked ? "1px solid #cbd5e1" : "none",
                                cursor: isLocked ? "not-allowed" : "pointer",
                                borderRadius: 4,
                              }}>
                              {isLocked ? "🔒 " : "Edit"}
                            </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 14 }}>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Bottom frozen mirror scrollbar ──
            Sits pinned to the bottom of the viewport (position: sticky)
            so it stays reachable while scrolling down a long list, instead
            of only being available once you scroll all the way past the
            table's own bottom edge. Synced both ways with the top mirror
            and the real table via syncScrollFromBottom above. */}
        <div
          ref={bottomScrollRef}
          onScroll={syncScrollFromBottom}
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            height: 16,
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid #e2e8f0",
            zIndex: 5,
          }}
        >
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>

        {/* ── Pagination (10 rows/page) ── */}
        {stockList.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 4px 4px" }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, stockList.length)} of {stockList.length}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "1px solid #e2e8f0",
                  background: currentPage === 1 ? "#f8fafc" : "#fff",
                  color: currentPage === 1 ? "#cbd5e1" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}>
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "…" ? (
                    <span key={`ellipsis-${idx}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 13 }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      style={{
                        minWidth: 30, padding: "6px 8px", fontSize: 13, borderRadius: 6,
                        border: p === currentPage ? "1px solid #021843" : "1px solid #e2e8f0",
                        background: p === currentPage ? "#021843" : "#fff",
                        color: p === currentPage ? "#fff" : "#334155",
                        cursor: "pointer", fontWeight: p === currentPage ? 700 : 400,
                      }}>
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "1px solid #e2e8f0",
                  background: currentPage === totalPages ? "#f8fafc" : "#fff",
                  color: currentPage === totalPages ? "#cbd5e1" : "#334155",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {viewItem && <ViewModal item={viewItem} moduleId={moduleId} onClose={() => setViewItem(null)} />}
      {editItem && <EditModal item={editItem} moduleId={moduleId} onClose={() => setEditItem(null)} onSaved={() => fetchStockList()} />}
      {showBulkConfirm && (
        <BulkInspectConfirmDialog
          count={selectedIds.length}
          saving={bulkSaving}
          onConfirm={confirmBulkInspect}
          onReject={confirmBulkReject}
          onCancel={() => !bulkSaving && setShowBulkConfirm(false)}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </Layout>
  );
}

// ─── Modal styles ─────────────────────────────────────────────────────────────
const mStyles = {
  backdrop:     { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, padding: 20 },
  modal:        { width: "95%", maxWidth: 1400, height: "92vh", background: "#ffffff", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.18)" },
  modalHeader:  { padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", position: "sticky", top: 0, zIndex: 10 },
  closeBtn:     { border: "none", background: "transparent", fontSize: 24, color: "#64748b", cursor: "pointer", width: 36, height: 36, borderRadius: 6, lineHeight: 1 },
  modalBody:    { flex: 1, overflowY: "auto", padding: "22px 24px", background: "#f8fafc" },
  modalFooter:  { padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", alignItems: "center", background: "#ffffff", position: "sticky", bottom: 0, zIndex: 10 },
  centreMsg:    { padding: 40, textAlign: "center", color: "#64748b" },
  sectionBox:   { background: "#fff", borderRadius: 8, padding: "14px 16px", marginBottom: 18, border: "1px solid #e2e8f0" },
  fieldLabel:   { fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 4, display: "block" },
  miniLabel:    { fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, display: "block", textTransform: "uppercase" },
  tabsRow:      { display: "flex", gap: 8, marginBottom: 14, marginTop: 4 },
  table:        { width: "100%", borderCollapse: "collapse", marginTop: 4, background: "#fff" },
  th:           { fontSize: 13, fontWeight: 700, color: "#475569", textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" },
  td:           { fontSize: 14, color: "#1e293b", padding: "8px 10px", borderBottom: "1px solid #f1f5f9" },
  inlineInput:  { border: "1px solid #cbd5e1", borderRadius: 4, padding: "3px 7px", fontSize: 12, width: "100%", minWidth: 90, outline: "none", height: 30 },
  saveBtn:      { background: "#021843", color: "#fff", border: "none", borderRadius: 8, padding: "10px 26px", fontSize: 14, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 6px rgba(2,24,67,0.2)" },
  docActionBtn: { padding: "6px 14px", fontSize: 13, fontWeight: 500, background: "#000", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  smallBtn:     { padding: "4px 12px", fontSize: 12, fontWeight: 500, background: "#021843", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" },
  passIcon:     { color: "#16a34a", fontSize: 20, fontWeight: 700, lineHeight: 1 },
  failIcon:     { color: "#dc2626", fontSize: 20, fontWeight: 700, lineHeight: 1 },
};

// ─── Page styles ──────────────────────────────────────────────────────────────
const pgStyles = {
  filterCard:     { background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 2px 8px rgba(15,23,42,.06)", border: "1px solid #e2e8f0", marginBottom: 18 },
  compactLabel:   { fontSize: 12, fontWeight: 600, marginBottom: 4, color: "#334155", display: "block" },
  tableContainer: { background: "#fff", borderRadius: 10, overflowX: "auto", overflowY: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,.08)", border: "1px solid #e2e8f0" },
  erpTh:          { background: "#f8fafc", fontSize: 12, fontWeight: 700, padding: "11px 12px", borderBottom: "2px solid #e2e8f0", color: "#475569", whiteSpace: "nowrap" },
  erpTd:          { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
  badge:          { background: "none", border: "none", padding: 0, color: "#334155", fontSize: 13, fontWeight: 500 },
};

export default Quality;